<template>
    <div :style="style" v-if="todisplay" v-show="isvisible" :class="{ 'c-AdvancedHistory': true, [`c-name-${this.name || 'unnamed'}`]: true, 'selection-locked': selectionLocked }">
        <Teleport to="body">
            <q-banner
                v-if="!!queryError"
                class="bg-negative text-white c-AdvancedReports-queryError"
                rounded
                dense
            >
                {{ queryError }}
                <template #action>
                <q-btn color="white" flat no-caps class="c-SentenceCaseButton" @click="queryError = ''">
                    {{ Translate('Close') }}
                </q-btn>
                </template>
            </q-banner>
        </Teleport>
        <div style="display: flex; flex-direction: row; flex-grow: 1;">
            <div v-if="!colDefsLoaded" style="display: flex; flex-direction: column; flex-grow: 1; background-color: lightGrey; align-items: center; justify-content: center;">
                <q-spinner color="purple" size="70px" :thickness="7" />
            </div>

            <div v-if="colDefsLoaded" style="display: flex; flex-direction: column; flex-grow: 1;">
                <div v-if="!controlData.IsDashboard">
                    <span class="q-mt-none q-ml-xs text-h6">{{ report_name }}</span>
                    <span v-if="selectionLocked" class="q-ml-md text-body2" style="color: grey;">{{ Translate("Close Ask AI to change the selection") }}</span>
                </div>

                <div v-if="!controlData.IsDashboard" class="gridHeaderButtons" :inert="selectionLocked || null">
                    <q-icon v-if="!ReadOnly" name="mdi-information" class="q-mr-sm form-hint-icon" size="sm" style="color: var(--v-checkboxGrey-base);">
                        <q-tooltip anchor="bottom middle" self="top middle">
                            {{Translate("Simulate the report using a range independent of the one set in the")}} 
                            <br />
                            {{Translate("Date/Time filter. Simulating has no effect on the published report.")}}
                        </q-tooltip>
                    </q-icon>
                    <MyCalendar v-if="dateRangePickerVisible"
                                :value="defaultTime"
                                :width="ReadOnly ? '430px' : '460px'"
                                :label="ReadOnly ? Translate('Select Date Range') : Translate('Simulate Date Range')"
                                activeicon="mdi-calendar"
                                :isactive="dateRangePickerVisible"
                                inactiveicon="mdi-calendar-alert"
                                :activecolor="''"
                                inactivecolor="grey lighten-1"
                                :showtooltip="!ReadOnly"
                                @change="applyDateRangeHandler">
                    </MyCalendar>

                    <q-input dense clearable hide-bottom-space @clear="clearSearch" class="q-ml-sm"
                             style="flex-grow: 0; min-width: 280px;"
                             :placeholder="Translate('Quick Search')"
                             v-model="search">
                        <template v-slot:prepend>
                            <q-icon name="mdi-magnify" />
                        </template>
                    </q-input>

                    <q-btn v-if="ReadOnly && IsAnyViewFilterChoices" flat no-caps
                        :color="tool_visible == 1 ? 'primary' : ''"
                        class="q-mx-sm c-SentenceCaseButton" @click="toggleDataFilters" style="font-size: 12px;">
                        <q-icon class="q-mr-sm" name="mdi-filter">
                            <span v-if="IsAnyFilters" style="
                                position: absolute;
                                bottom: 0px;
                                right: -4px;
                                width: 8px;
                                height: 8px;
                                background-color: var(--q-primary);
                                border-radius: 50%;
                            " />
                        </q-icon>
                        <span>{{ Translate("Filters") }}</span>
                    </q-btn>

                    <q-space></q-space>

                    <q-spinner v-if="false && query_in_progress" color="primary" size="20px" class="q-mr-xl q-ml-xl" />

                    <span v-if="this.SelectedRowCount" class="q-pr-md text-body2" style="color: grey;">
                        ({{ this.SelectedRowCount }} {{ Translate("Selected") }}
                        <q-btn flat dense icon="mdi-close-circle" @click="ClearSelectedRows" :title="Translate('Clear Selected Rows')" />)
                    </span>

                    <span v-if="System.DEBUG" :title="Translate('Total Rows')" class="q-pr-md text-body2" style="color: grey;">({{ totalHits }})</span>

                    <q-btn size="sm" outline no-caps class="q-mr-md" color="grey-8" @click="cycleDurationFormats" style="font-size: 12px;">
                        {{ durationFormats[durationFormatIdx].text }}
                        <q-tooltip anchor="bottom middle" self="top middle">
                            {{ Translate("Duration Time Format") }}
                            <br />
                            {{ Translate("Click to cycle through formats") }}
                        </q-tooltip>
                    </q-btn>

                    <q-btn v-if="false && ReadOnly && IsAnyFilters" flat icon="mdi-filter" color="primary" :title="Translate('Filter(s) Active')" />

                    <q-btn flat class="q-ml-none" @click="autoSizeAllColumns">
                        <q-icon name="mdi-arrow-left-right-bold" :title="Translate('Size columns to content')" />
                    </q-btn>

                    <q-btn flat class="q-ml-none" @click="sizeColumnsToFit">
                        <q-icon name="mdi-arrow-expand-horizontal" :title="Translate('Size columns to fit')" />
                    </q-btn>

                    <q-btn :disable="query_in_progress" flat class="q-ml-none" @click="Refresh">
                        <q-icon name="mdi-cached" :title="Translate('Refresh')" :class="{ 'icon-spinner': query_in_progress }" />
                    </q-btn>

                    <ColumnMenu v-if="colDefsLoaded && ReadOnly"
                                :column_list="column_picker_list"
                                :column_order="column_state.columnOrder"
                                :columnDefs="columnDefs"
                                :column_default_visibility="column_default_visibility"
                                :setColumnsVisible="setColumnsVisible"
                                :debugMode="System.DEBUG"
                                @showcolumnselector="showColumnSelector"
                                @showdatafilters="showDataFilters" />
                </div>

                <div v-if="controlData.IsDashboard" class="gridHeaderButtons">
                    <MyCalendar v-show="dateRangePickerVisible"
                                :key="id"
                                :value="defaultTime"
                                width="430px"
                                :label="ReadOnly ? Translate('Select Date Range') : Translate('Simulate Date Range')"
                                :isactive="true"
                                activeicon="mdi-calendar"
                                @change="applyDateRangeHandler" />

                    <q-input dense clearable hide-bottom-space @clear="clearSearch" class="q-ml-sm quick-search-input"
                             :placeholder="Translate('Quick Search')"
                             v-model="search">
                        <template v-slot:prepend>
                            <q-icon name="mdi-magnify" />
                        </template>
                    </q-input>

                    <q-space></q-space>

                    <q-btn 
                        v-if="ReadOnly" 
                        icon="mdi-filter" 
                        flat 
                        round
                        class="q-ml-none" 
                        :color="tool_visible == 1 ? 'primary' : ''" 
                        @click="toggleDataFilters"
                    >
                        <q-badge color="primary" rounded class="absolute" style="bottom: 6px; right: 4px;" v-if="IsAnyFilters" />
                    </q-btn>

                    <q-btn size="sm" outline no-caps color="grey-8" @click="cycleDurationFormats">
                        {{ durationFormats[durationFormatIdx].text }}
                        <q-tooltip anchor="bottom middle" self="top middle">
                            {{ Translate("Duration Time Format") }}
                            <br />
                            {{ Translate("Click to cycle through formats") }}
                        </q-tooltip>
                    </q-btn>

                    <q-btn flat class="q-ml-none" @click="autoSizeAllColumns">
                        <q-icon name="mdi-arrow-left-right-bold" :title="Translate('Size columns to content')" />
                    </q-btn>

                    <q-btn flat class="q-ml-none" @click="sizeColumnsToFit">
                        <q-icon name="mdi-arrow-expand-horizontal" :title="Translate('Size columns to fit')" />
                    </q-btn>

                    <q-btn :disable="query_in_progress" flat class="q-ml-none" @click="Refresh">
                        <q-icon name="mdi-cached" :title="Translate('Refresh')" :class="{ 'icon-spinner': query_in_progress }" />
                    </q-btn>

                    <ColumnMenu v-if="colDefsLoaded && ReadOnly"
                                :column_list="column_picker_list"
                                :column_order="column_state.columnOrder"
                                :columnDefs="columnDefs"
                                :column_default_visibility="column_default_visibility"
                                :setColumnsVisible="setColumnsVisible"
                                :debugMode="System.DEBUG"
                                :btnclass="{ 'q-mr-sm': false }"
                                @showcolumnselector="showColumnSelector"
                                @showdatafilters="showDataFilters" />
                </div>

                <AgGridVue _key="unique_id"
                           v-if="colDefsLoaded" v-show="!nocolumns"
                           :class="{ 'ag-theme-alpine': true }"
                           :style="{ display: 'flex', flexDirection: 'column', flexGrow: '1' }"
                           :initialState="initialState"
                           :columnDefs="columnDefs"
                           :defaultColDef="defaultColDef"
                           :serverSideDatasource="dataSource"
                           :rowSelection="agRowSelection"
                           :selectionColumnDef="selectionColumnDef"
                           @selectionChanged="rowSelectionChanged"
                           :cacheBlockSize="pageSize"
                           :enableCellTextSelection="true"
                           :sideBar="sideBar"
                           :aggFuncs="aggFuncs"
                           :cellSelection="true"
                           :columnTypes="dataTypeDefinitions"
                           :autoGroupColumnDef="autoGroupColumnDef"
                           :maintainColumnOrder="true"
                           :treeData="treeData"
                           :getRowClass="getRowClass"
                           rowModelType="serverSide"
                           serverSidePivotResultFieldSeparator="~"
                           @gridReady="onGridReady"
                           @stateUpdated="stateUpdated"
                           @firstDataRendered="onFirstDataRendered"
                           @columnMoved="onColumnMoved" />

                <div v-if="colDefsLoaded && nocolumns"
                     style="flex-grow: 1; background-color: white; border: 1px solid var(--v-lightGreyBorder-base); display: flex; flex-direction: column; align-content: center; align-items: center; justify-content: center;">
                    <div style="max-width: 280px; display: flex; flex-direction: column;" class="text-caption text-center">
                        <q-icon style="font-size: 80px; color: var(--v-secondaryText-base); align-self: center;" class="q-mb-lg" name="mdi-finance" />
                        {{ Translate('Start building your report by selecting columns from the panel on the right') }}
                    </div>
                </div>
            </div>

            <ToolReportPanelShell v-if="colDefsLoaded && !ReadOnly"
                                  v-show="tool_visible === 0"
                                  report-mode="detail"
                                  :expansion-group="unique_id"
                                  :isvisible="true"
                                  :settings="settings"
                                  :column_list="column_list"
                                  :filter_column_list="column_filter_list"
                                  :filter-key="unique_id"
                                  :column_filters="column_filters"
                                  :initial_column_filters="initial_column_filters"
                                  :threshold_rules="threshold_rules"
                                  :report_settings="report_model"
                                  :report_column_list="visibleColumnIds"
                                  :source="source_selected"
                                  :sourceTypes="sourceTypes"
                                  :source_selected="source_selected"
                                  :pageSize="pageSize"
                                  :initial_report_name="report_name"
                                  :initial_report_description="report_description"
                                  :initial_report_category="report_category"
                                  :initial_show_manage_categories="show_manage_categories"
                                  :control_category_actions="control_category_actions"
                                  :EventBus="EventBus"
                                  :debugMode="System.DEBUG"
                                  @namechanged="reportNameChanged"
                                  @descriptionchanged="reportDescChanged"
                                  @categorychanged="reportCategoryChanged"
                                  @sourcechanged="(v) => { source_selected = v; loadColumns(v, true); }"
                                  @pagesizechanged="(v) => { pageSize = v; modelChanged(); }"
                                  @filterschanged="notifyfilterschanged"
                                  @update:column_rules="notifyThresholdsChanged">
                <template #betweenDetailsAndFilters>
                    <q-expansion-item 
                            :group="unique_id" 
                            class="report-expansion-item" 
                            expand-icon="mdi-menu-down" 
                            header-class="text-h6" 
                            header-style="height: 64px;" 
                            style="border-bottom: 1px solid var(--v-lightGreyBorder-base);" 
                            :label="Translate('Columns')">
                        <q-card-section>
                            <ToolReportColumns :key="unique_id"
                                               :isvisible="true"
                                               :ReadOnly="ReadOnly"
                                               :settings="settings"
                                               :column_list="column_picker_list"
                                               :columnDefs="columnDefs"
                                               :column_default_visibility="column_default_visibility"
                                               :setColumnsVisible="setColumnsVisible"
                                               :setColumnsDefaultHidden="setColumnsDefaultHidden"
                                               :storageKey="storageKey"
                                               :source="source_selected"
                                               :debugMode="System.DEBUG"
                                               :column_order="column_state.columnOrder"
                                               @columnsordered="columnsordered" />
                        </q-card-section>
                    </q-expansion-item>
                </template>
            </ToolReportPanelShell>

            <div v-if="colDefsLoaded && ReadOnly && FilterModal">
                <q-dialog :model-value="tool_visible === 1" persistent>
                    <q-card class="bg-grey-2" style="max-height: 650px; max-width: 600px; width: 100%;">
                        <q-toolbar class="bg-navigation" style="background-color: var(--v-navigation-base); height: 60px">
                            <div class="title text-white q-pl-none">
                                {{ Translate('Report Filters') }}
                            </div>
                            <q-space></q-space>
                        </q-toolbar>

                        <q-card-section class="q-mt-sm">
                            <div style="overflow: auto; width: 100%; display: flex; flex-direction: column; flex-basis: 0; flex-grow: 1;">
                                <ToolReportFilters :key="unique_id"
                                                   :isvisible="true"
                                                   :ReadOnly="ReadOnly"
                                                   :settings="settings"
                                                   :column_list="column_filter_list"
                                                   :column_filters="column_filters"
                                                   :initial_column_filters="initial_column_filters"
                                                   :source="source_selected"
                                                   :EventBus="EventBus"
                                                   @filterschanged="notifyfilterschanged" />
                            </div>
                        </q-card-section>

                        <q-card-actions class="q-ma-sm" style="justify-content: flex-end;">
                            <q-btn flat no-caps color="primary" class="c-SentenceCaseButton" @click="tool_visible = undefined">
                                {{ Translate('Done') }}
                            </q-btn>
                        </q-card-actions>
                    </q-card>
                </q-dialog>
            </div>

            <div v-if="colDefsLoaded && ReadOnly && !FilterModal" v-show="tool_visible === 1"
                 class="c-ToolPanel_1 q-mx-auto w-100 q-pa-none history-panel-width"
                 style="width: 100%; display: flex; flex-direction: column;">
                <div style="overflow: auto; width: 100%; display: flex; flex-direction: column; flex-basis: 0; flex-grow: 1;">
                    <q-list>
                        <q-card flat>
                            <q-item dense class="q-px-lg q-py-md items-center report-filters-header">
                                <span class="text-h6">{{ Translate('Filters') }}</span>
                                <q-space />
                                <q-btn dense flat no-caps color="primary" class="q-mr-sm q-px-sm c-SentenceCaseButton" :title="Translate('Clear All Filters')"
                                       v-if="ReadOnly && IsAnyFilters"
                                       @click.stop="clearAllFilters"><span>{{ Translate("Clear All") }}</span></q-btn>
                                <q-icon v-if="ReadOnly" name="mdi-close" class="cursor-pointer" size="sm" :title="Translate('Close')" @click="tool_visible = undefined" />
                            </q-item>

                            <q-card-section>
                                <ToolReportFilters :key="unique_id"
                                                   :isvisible="true"
                                                   :ReadOnly="ReadOnly"
                                                   :settings="settings"
                                                   :column_list="column_filter_list"
                                                   :column_filters="column_filters"
                                                   :initial_column_filters="initial_column_filters"
                                                   :source="source_selected"
                                                   :EventBus="EventBus"
                                                   @filterschanged="notifyfilterschanged" />
                            </q-card-section>
                        </q-card>
                    </q-list>
                </div>
            </div>

            <q-card v-if="colDefsLoaded && tool_visible === 2" flat class="c-ToolPanel_1 q-mx-auto w-100 q-pa-none history-panel-width" style="width: 100%; display: flex; flex-direction: column;">
                <q-card class="q-mx-auto w-100 q-pa-none" style="overflow: auto; width: 100%; display: flex; flex-direction: column; flex-basis: 0; flex-grow: 1; background-color: white;" flat>
                    <q-card-section>
                        {{ Translate('Report Settings JSON') }}
                    </q-card-section>
                    <q-card-section>
<pre>
{{ JSON.stringify(ReportModel,null,3) }}
</pre>
                    </q-card-section>
                </q-card>
            </q-card>

            <div v-if="colDefsLoaded && !ReadOnly" style="display: flex; flex-direction: column; margin-right: -8px;">
                <q-btn color="extraLightGrey" size="md" style="border: 1px solid; border-radius: 0px;" @click="tool_visible = (tool_visible !== 0) ? 0 : undefined" unelevated>
                    <q-icon name="mdi-cogs" :title="Translate('Column Selector')" :color="tool_visible === 0 ? 'primary': 'grey-5'" />
                </q-btn>
                <q-btn color="extraLightGrey" size="md" v-if="System.DEBUG" style="border: 1px solid; border-radius: 0px;" @click="tool_visible = (tool_visible !== 2) ? 2 : undefined" unelevated>
                    <q-icon name="mdi-code-json" :title="Translate('Model')" :color="tool_visible === 2 ? 'primary': 'grey-5'" />
                </q-btn>
            </div>
        </div>

    </div>
</template>

<script>
    /* eslint-disable */

    import * as Vue from "vue";

    import utils from '../../Shared/utils.jsx';
    import helps from '../careHelpfulFunctions.jsx';
    import BaseComponent from './BaseComponentMixin.jsx';
    import AgContentDef from './AgContentDef.jsx';

    import _ from 'lodash';

    import 'ag-grid-enterprise';
    import dayjs from 'dayjs';
    import { AgGridVue } from "ag-grid-vue3";
    import CustomBooleanCellRenderer from "./vuecontrols/advancedHistory/CustomBooleanCellRenderer.vue";

    import ToolReportColumns from './vuecontrols/advancedHistory/agGridToolReportColumns.vue';
    import ToolReportFilters from './vuecontrols/advancedHistory/agGridToolReportFilters.vue';
    import ToolReportPanelShell from './vuecontrols/advancedHistory/agGridToolReportPanelShell.vue';
    import IconComponent from './vuecontrols/advancedHistory/iconComponent.vue';
    import ColumnMenu from './vuecontrols/advancedHistory/columnMenu.vue';
    import MyCalendar from './vuecontrols/calendar.vue';

    import common from './vuecontrols/advancedHistory/common.js';
    import { ColumnsToolPanelModule } from "ag-grid-enterprise";

    const createEventBus = () => {
        const listeners = new Map();
        return {
            $on(event, handler) {
                if (!listeners.has(event)) listeners.set(event, new Set());
                listeners.get(event).add(handler);
            },
            $off(event, handler) {
                if (!listeners.has(event)) return;
                if (!handler) listeners.delete(event);
                else listeners.get(event).delete(handler);
            },
            $once(event, handler) {
                const wrapped = (...args) => {
                    this.$off(event, wrapped);
                    handler(...args);
                };
                this.$on(event, wrapped);
            },
            $emit(event, ...args) {
                if (!listeners.has(event)) return;
                for (const handler of listeners.get(event)) handler(...args);
            }
        };
    };

    // If we move IconComponent into its own file, we can use .vue or .jsx and we
    // won't have to use the render function in this manner:
    const IconComponent0 = { // inline custom component for ag-grid
        methods: {
            sessionId() {
                return this.params && this.params.data ? JSON.stringify(this.params.data) : '...';
            },
            buttonClick(e) {
                alert('clicked ' + this.sessionId());
            },
        },
        render(h) {
            // h is a function that we call to create instances of any UI components.
            // Returning a button with an icon inside that can be clicked.
            const i = h('i',
                {
                    ['aria-hidden']: 'true',
                    class: 'v-icon notranslate mdi mdi-mdi mdi-magnify-plus-outline theme--light',
                    style: 'font-size: 20px;'
                }
            )
            const span = h('span',
                {
                    class: 'v-btn__content'
                },
                [i]
            );
            const button =
                h('button',
                    {
                        type: 'button',
                        class: 'v-btn v-btn--icon v-btn--round theme--light elevation-0 v-size--default secondary--text',
                        medium: 'true',
                        on: {
                            click: (e) => this.buttonClick(e)
                        }
                    },
                    [span]
                );

            return button;
        }
    };

    export default {
        name: "AdvancedHistory",
        mixins: [BaseComponent],
        components: {
            AgGridVue,
            CustomBooleanCellRenderer, 
            AgContentDef,
            IconComponent,
            ToolReportColumns,
            ToolReportFilters,
            ToolReportPanelShell,
            ColumnMenu,
            MyCalendar,
        },
        data: function () {
            return {
                unique_id: utils.generateUUID(),
                id: 1,
                datespan: '12/01/2023 12:00:00 AM - 12/01/2023 11:59:59 PM',
                useStartEnd: false,
                query_elapsed: '',
                query_result_size: 0,
                query_in_progress: false,
                query_refresh_pending: false,
                refreshOnReady: false,
                queryError: '',
                timezone: '',
                delay_expn: 0,
                frameworkComponents: {
                    CustomBooleanCellRenderer: CustomBooleanCellRenderer,
                },
                panelactive: 0,
                initialState: null,

                datamodel: null,
                report_model: null,
                defaultTime: 'today',
                baseurl: 'Apps/ReportStudio/AdvancedReporting/QueryDetail',
                //startDate : '2024-02-01T00:09:00.0000000Z',
                //endDate: '2024-03-01T00:09:00.0000000Z',
                search: '',
                active_source: 'contact_data_points3',
                active_interval: '1h',
                autoGroupColumnDef: null,
                colDefsLoaded: false,
                columnDefs: [ ],
                visibleColumnIds: [],
                columnVisibility: {},
                customerLookup: {},
                customMetrics: null,

                selectedRows: [],
                selectedRowsData: [],
                selectRowsExpr: null,
                selectionLockedExpr: null,
                // Stable identities — inline object literals re-apply AG Grid's selection column
                // on every parent render (e.g. buoy icon doesFileExist → PCAPExists), which
                // remounts IconComponent and closes open q-menus while swapping checkbox/icon order.
                selectionColumnDef: {
                    pinned: 'left',
                    lockPosition: 'left',
                    suppressMovable: true,
                    width: 40,
                    suppressKeyboardEvent: (params) => this.selectionLocked && params.event.key === ' ',
                },
                rowSelectionConfig: {
                    mode: 'multiRow',
                    headerCheckbox: false,
                    // Match Vue2 filtered select-all / AG Grid 34 MultiRowSelectionOptions
                    selectAll: 'filtered',
                    enableClickSelection: false,
                    isRowSelectable: null,
                },
                leadingPinnedColIds: ['ag-Grid-SelectionColumn', 'ah-icons'],

                filter_settings: {
                    datetime: {
                        filterType: 'number',
                        type: 'inRange',
                        filter: 'now/d',
                        filterTo: 'now',
                        usage: 1
                    },
                    extralegtype: {
                        filterType: 'set',
                        values: ['Agent'],
                        mode: 'exclusive',
                        usage: 0
                    },
                },
                column_state: {},
                column_default_visibility: {},

                // These settings are passed down the chain of components
                settings: {
                    startDate: '2024-02-01T00:09:00.0000000Z',
                    endDate: '2024-03-01T00:09:00.0000000Z',
                    pageSize: 100,
                    presortSize: 1000,
                    serverPaginationMode: 1,
                },

                tool_visible: 0,
                column_list: [],
                column_filters: {
                    datetime: {
                        filterType: 'number',
                        type: 'inRange',
                        filter: 'now/d',
                        filterTo: 'now',
                        usage: 1
                    },
                    extralegtype: {
                        filterType: 'set',
                        values: ['Agent'],
                        mode: 'exclusive',
                        usage: 0
                    },
                },
                threshold_rules: [],
                initial_column_filters: null,

                viewonly_settings: {
                    column_picker_list: [],
                    column_filter_list: [],
                },

                durationFormatIdx: 0,

                source_selected: 'contacts',
                sourceTypes: [],


                pageSize: 100,

                defaultColDef: {
                    // set the default column width
                    width: 150,
                    // make every column editable
                    editable: false,
                    // make every column use 'text' filter by default
                    filter: 'agTextColumnFilter',
                    // enable floating filters by default
                    //floatingFilter: true,
                    // disable cell data types
                    //cellDataType: false,
                    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
                    cellStyle: { textAlign: 'left' },
                    defaultAggFunc: 'avg',
                    // Space toggles a row's selection on any focused cell, even with the checkboxes locked
                    suppressKeyboardEvent: (params) => this.selectionLocked && params.event.key === ' ',
                },
                aggFuncs: {
                    cardinality: params => { return 0; },
                },
                dataTypeDefinitions: {
                    // override `date` to handle custom date format `dd/mm/yyyy`
                    dateColumn: {
                        filter: 'agDateColumnFilter',
                      //cellDataType: 'date',
                        resizable: true,
                        sortable: true,
                        keyCreator: params => {
                            if (common.isBlankDateValue(params.value)) return '';
                            return new Date(params.value).toISOString();
                        },
                        valueParser: params => {
                            if (params.newValue == null) {
                                return null;
                            }
                            // convert from ISO-8601
                            return new Date(params.newValue);
                        },
                        valueFormatter: params => {
                            // convert to locale date string
                            //debugger;
                            let options;
                            if (this.timezone)
                                options = { timeZone: this.timezone };

                            // MinValue may arrive as "0001-01-01T00:00:00" or "...Z" (lab OS); use year check.
                            if (common.isBlankDateValue(params.value)) return '';
                            if (common.isNeverDateValue(params.value)) return this.Translate('Never');
                            return new Date(params.value).toLocaleDateString(undefined, options) + ' ' +
                                new Date(params.value).toLocaleTimeString(undefined, options);
                        },
                        suppressHeaderMenuButton: false,
                    },
                    dateOnlyColumn: {
                        filter: 'agDateColumnFilter',
                        //cellDataType: 'date',
                        resizable: true,
                        sortable: true,
                        keyCreator: params => {
                            if (common.isBlankDateValue(params.value)) return '';
                            return new Date(params.value).toISOString();
                        },
                        valueParser: params => {
                            if (params.newValue == null) {
                                return null;
                            }
                            // convert from ISO-8601
                            return new Date(params.newValue);
                        },
                        valueFormatter: params => {
                            // convert to locale date string
                            //debugger;
                            let options;
                            if (this.timezone)
                                options = { timeZone: this.timezone };

                            if (common.isBlankDateValue(params.value)) return '';
                            if (common.isNeverDateValue(params.value)) return this.Translate('Never');
                            return new Date(params.value).toLocaleDateString(undefined, options);
                        },
                        suppressHeaderMenuButton: false,
                    },
                    timeOnlyColumn: {
                        filter: 'agDateColumnFilter',
                        //cellDataType: 'date',
                        resizable: true,
                        sortable: true,
                        keyCreator: params => {
                            if (common.isBlankDateValue(params.value)) return '';
                            return new Date(params.value).toISOString();
                        },
                        valueParser: params => {
                            if (params.newValue == null) {
                                return null;
                            }
                            // convert from ISO-8601
                            return new Date(params.newValue);
                        },
                        valueFormatter: params => {
                            // convert to locale date string
                            //debugger;
                            let options;
                            if (this.timezone)
                                options = { timeZone: this.timezone };

                            if (common.isBlankDateValue(params.value)) return '';
                            if (common.isNeverDateValue(params.value)) return this.Translate('Never');
                            return new Date(params.value).toLocaleTimeString(undefined, options);
                        },
                        suppressHeaderMenuButton: false,
                    },
                    searchableTextColumn: {
                        filter: 'agTextColumnFilter',
                      //cellDataType: 'text',
                        resizable: true,
                        sortable: true,
                        filterParams: { debounceMs: 1000 },
                    },
                    directionColumn: {
                        sortable: true,
                        resizable: true,
                        enablePivot: true,
                        filter: 'agSetColumnFilter',
                        filterParams: { values: ['inbound', 'outbound'] },
                        cellClass: 'text-capitalize',
                    },
                    sparklineColumn: {
                        sortable: false,
                        resizable: true,
                        enablePivot: false,
                        cellRenderer: 'agSparklineCellRenderer',
                        cellRendererParams: {
                            sparklineOptions: {
                                type: 'area'
                            }
                        },
                        valueGetter: (params) => {
                            var colField = params.colDef.field;

                            var parts = colField.split('.');
                            var value = params.data;

                            for (var i = 0; i < parts.length; i++) {
                                if (value) {
                                    value = value[parts[i]];
                                }
                            }
                            
                            if (!value)
                                return [];

                            return value.map(v => v.value);
                        }
                    },
                    booleanColumn: {
                        sortable: true,
                        resizable: true,
                        cellDataType: 'boolean',
                        filter: 'agSetColumnFilter',
                        filterParams: { 
                            values: [ 'true', 'false' ],
                            valueFormatter: params => this.booleanFormatter(params)
                        },
                        // cellRenderer: 'CustomBooleanCellRenderer', This is replaced by booleanFormatter - Jeff E. 3/17/26
                        valueFormatter: params => this.booleanFormatter(params),
                        cellRendererParams: (params) => {
                            let color = null;
                            this.report_model.thresholds?.forEach(threshold => {
                                if (threshold.id === params.colDef.colId)
                                {
                                    switch(threshold.type)
                                    {
                                        case "direction":
                                        {
                                            var thresholdDirection = threshold.value?.toLowerCase();
                                            var cellValue = threshold.value?.toLowerCase();
                                            if (thresholdDirection === cellValue)
                                            {
                                                color = threshold.color;
                                            }          
                                        }
                                        case "boolean":
                                        {
                                            var cellValue = params.value;
                                            if (String(cellValue).toLowerCase() == threshold.value?.toLowerCase())
                                            {
                                                color = threshold.color;
                                            }           
                                            else 
                                            {
                                                if (!threshold.value && !cellValue)
                                                {
                                                    color = threshold.color;
                                                }   
                                            }                                     
                                        }
                                    }
                                }
                            });
                            return {
                                customBooleanColor: color
                            };     
                        }                      
                    },
                    textSetColumn: {
                        sortable: true,
                        resizable: true,
                        enablePivot: true,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            refreshValuesOnOpen: true
                        }
                    },
                    channelTypeColumn: {
                        sortable: true,
                        resizable: true,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: this.getUniqueColValues('timelinetype'),
                            refreshValuesOnOpen: true,
                            valueFormatter: params => {
                                if (params.value && params.value.substring(0, 14) == 'CustomChannel~')
                                    return params.value.substring(14);
                                else
                                    return params.value;
                            },
                            comparator: (x,y) => {
                                let a = x;
                                let b = y;
                                if (a.substring(0, 14) == 'CustomChannel~') a = a.substring(14);
                                if (b.substring(0, 14) == 'CustomChannel~') b = b.substring(14);
                                return a > b ? 1 : a < b ? -1 : 0;
                            }
                        },
                        valueFormatter: params => {
                            if (params.value && params.value.substring(0,14) == 'CustomChannel~')
                                return params.value.substring(14);
                            else
                                return params.value;
                        },
                    },
                    durationColumn: {
                        sortable: true,
                        resizable: true,
                        enableValue: true,
                        filter: 'agNumberColumnFilter',
                        cellClass: 'right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        valueFormatter: params => {
                            let seconds = params.value;
                            if (!(typeof seconds === 'number'))
                                return '';

                            // Round the input seconds to the nearest whole number
                            seconds = Math.round(seconds);

                            let result = "";

                            switch (this.durationFormat) {
                                case 'HH:MM:SS':
                                    let neg = false;
                                    if (seconds < 0) {
                                        seconds = -seconds;
                                        neg = true;
                                    }
                                    seconds = Math.floor(seconds);

                                    let hours = Math.floor(seconds / (60 * 60));
                                    seconds = seconds - (hours * 60 * 60);

                                    let minutes = Math.floor(seconds / 60);
                                    seconds = seconds - (minutes * 60);

                                    if (hours)
                                        result = ((hours <= 9) ? "0" : "") + hours + ":" + ((minutes <= 9) ? "0" : "") + minutes + ":" + ((seconds <= 9) ? "0" : "") + seconds;
                                    else if (minutes)
                                        result = ((minutes <= 9) ? "0" : "") + minutes + ":" + ((seconds <= 9) ? "0" : "") + seconds;
                                    else if (seconds)
                                        result = "00:" + ((seconds <= 9) ? "0" : "") + seconds;
                                    else
                                        result = "00:00";

                                    if (neg) result = `-${result}`;
                                    break;

                                case 'Minutes':
                                    result = (seconds / 60).toFixed(2);
                                    break;

                                case 'Seconds':
                                default:
                                    result = seconds.toFixed(0);
                                    break;
                            }

                            return result;
                        }
                    },
                    countColumn: {
                        sortable: true,
                        resizable: true,
                        enableValue: true,
                        filter: 'agNumberColumnFilter',
                        cellClass: 'right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                    },
                    floatColumn: {
                        sortable: true,
                        resizable: true,
                        enableValue: true,
                        filter: 'agNumberColumnFilter',
                        cellClass: 'right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        valueFormatter: params => {
                            if (typeof params.value === 'number')
                                return params.value.toFixed(1);
                            else
                                return '';
                        }
                    },
                    percentColumn: {
                        sortable: true,
                        resizable: true,
                        enableValue: true,
                        filter: 'agNumberColumnFilter',
                        cellClass: 'right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        valueFormatter: params => {
                            if (typeof params.value !== 'number')
                                return '';
                            // IVR Data Points store the raw number the flow wrote, not a fraction
                            const col = common.column_lookup[params.colDef.colId];
                            if (col && (col.Sources || []).includes('ivr_data_points'))
                                return params.value.toFixed(1) + '%';
                            return (params.value * 100).toFixed(1) + '%';
                        }
                    },
                    customerIDColumn: {
                        sortable: true,
                        resizable: true,
                        valueFormatter: this.customerLookupFormatter,
                        enablePivot: true,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: this.getUniqueColValues('ownerid'),
                            valueFormatter: this.customerLookupFormatter,
                            comparator: this.customerLookupComparator
                        }
                    },
                    userIDColumn: {
                        sortable: true,
                        resizable: true,
                        valueFormatter: this.userLookupFormatter,
                        enablePivot: true,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            values: this.getUniqueColValues('userid'),
                            valueFormatter: this.userLookupFormatter,
                            comparator: this.userLookupComparator
                        }
                    },
                    currencyColumn: {
                        sortable: true,
                        resizable: true,
                        enableValue: true,
                        filter: 'agNumberColumnFilter',
                        cellClass: 'right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        valueFormatter: params => {
                            if (typeof params.value === 'number')
                                return this.f_currency(params.value);
                            else
                                return '';
                        }
                    },
                    phoneNumColumn: {
                        sortable: true,
                        resizable: true,
                        valueFormatter: this.phoneNumLookupFormatter,
                        enablePivot: true,
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            //values: this.getUniqueColValues('userid'),
                            valueFormatter: this.phoneNumLookupFormatter,
                            comparator: this.phoneNumLookupComparator
                        }
                    },
                    computedColumn: {
                        filter: 'agTextColumnFilter',
                        resizable: true,
                        sortable: true,
                    }
                },
                treeData: false, // true,
                groupColDef_byMasterID: {
                    filter: 'agGroupColumnFilter',
                    headerName: 'Start Date',
                    menuTabs: ['generalMenuTab'],
                    cellRendererParams: {
                      innerRenderer: (params) => {
                        if (common.isBlankDateValue(params.data?.starttime)) return '';
                        if (common.isNeverDateValue(params.data.starttime)) return this.Translate('Never');
                        return new Date(params.data.starttime).toLocaleDateString() + ' ' + new Date(params.data.starttime).toLocaleTimeString();
                      },
                    },
                },
                groupColDef_Normal: {
                    filter: 'agGroupColumnFilter',
                },
                
                after_key: null, // stores the "next page" pagination key for aggregate data sets
                query_duration: 0,
                total_rows: 0,
                using_aggs: false,
                dataSource: {
                    getRows: async (server) => {
                        const params = server.request;
                        console.log(`params: ${JSON.stringify(params)}`);

                        // params: {"startRow":0,"endRow":100,"sortModel":[{"sort":"asc","colId":"starttime"}],"filterModel":{}}
                        // params: {"startRow":0,"endRow":100,"sortModel":[{"sort":"desc","colId":"prequeuetime"}],
                        //          "filterModel":{"callerid":{"filterType":"text","type":"contains","filter":"5432"}}}

                        const output = await this.queryFlatTable(params);
                        server.success(output);
                    }
                },

                report_name: '',
                report_description: '',
                report_category: null,
                show_manage_categories: false,
                control_category_actions: null,
                isPillReordering: false,
            }
        },
        created() {
            this.EventBus = createEventBus();
            this.rowSelectionConfig.isRowSelectable = () => !!this.selectRowsEnabled;

            if (this.controlData && this.controlData.SelectableRowsExpression)
                this.selectRowsExpr = utils.compileExpression(this, this.controlData.SelectableRowsExpression);

            if (this.controlData && this.controlData.SelectionLockedExpression)
                this.selectionLockedExpr = utils.compileExpression(this, this.controlData.SelectionLockedExpression);

            if ((this.controlData.DataType === 'URL' || this.controlData.DataType === undefined) && this.controlData.DataURL) {
                this.modelurl = utils.compile(this, this.controlData.DataURL);
            }
            else if (this.controlData.DataType === 'RawInterpolated' && this.controlData.DataRaw) {
                this.modelvalue = utils.compileObject(this, this.controlData.DataRaw);
            }

            if (this.controlData.BaseQueryURL) {
                try {
                    const baseurl_expn = utils.compile(this, this.controlData.BaseQueryURL)
                    this.baseurl = utils.evaluate(baseurl_expn, this);
                }
                catch (err) {
                    console.log('Error interpolating BaseQueryURL: ' + this.controlData.BaseQueryURL + '; error: ' + err.message);
                }
            }

            if (this.ReadOnly)
                this.tool_visible = undefined;

            this.groupColDef_byMasterID.headerName = this.Translate('Start Date');

            if (this.controlData.RefreshInterval)
                this.delay_expn = utils.compileExpression(this, this.controlData.RefreshInterval);

            if (this.controlData.Icons)
                this.precompileIconActions();

            if (this.controlData.ReportName)
                try {
                    let expn = utils.compile(this, this.controlData.ReportName);
                    this.report_name = utils.evaluate(expn, this);
                }
                catch (err) {
                    console.log(err.message);
                }

            if (this.controlData.ReportDescription)
                try {
                    let expn = utils.compile(this, this.controlData.ReportDescription);
                    this.report_description = utils.evaluate(expn, this);
                }
                catch (err) {
                    console.log(err.message);
                }

            if (this.controlData.Category)
                try {
                    let expn = utils.compile(this, this.controlData.Category);
                    this.report_category = utils.evaluate(expn, this) || null;
                }
                catch (err) {
                    console.log(err.message);
                }

            if (this.controlData.PublishReportName) {
                let vueSet = utils.helpers.convertSetValueToVueSet(this.controlData.PublishReportName, 'value');
                this.setReportNameFunction = new Function('value', 'context', 'util', 'vue', `with (context) { ${vueSet} }`);
            }
            if (this.controlData.PublishReportDescription) {
                let vueSet = utils.helpers.convertSetValueToVueSet(this.controlData.PublishReportDescription, 'value');
                this.setReportDescriptionFunction = new Function('value', 'context', 'util', 'vue', `with (context) { ${vueSet} }`);
            }
            if (this.controlData.PublishCategory) {
                let vueSet = utils.helpers.convertSetValueToVueSet(this.controlData.PublishCategory, 'value');
                this.setReportCategoryFunction = new Function('value', 'context', 'util', 'vue', `with (context) { ${vueSet} }`);
            }

            if (this.controlData.ShowManageCategories)
                try {
                    let expn = utils.compile(this, this.controlData.ShowManageCategories);
                    let val = utils.evaluate(expn, this);
                    this.show_manage_categories = val === true || val === "true";
                }
                catch (err) {
                    console.log(err.message);
                }

            if (this.controlData.CategoryActions)
                this.control_category_actions = this.controlData.CategoryActions;
        },
        //Created Replaced with preRenderComplete
        computed: {
            ReadOnly: function () {
                return !!this.controlData.ReadOnly;
            },
            FilterModal: function () {
                return !!this.controlData.FilterModal;
            },
            dateRangePickerVisible: function () {
                if (this.filter_settings && (this.filter_settings.starttime || this.filter_settings.datetime)) {
                    const f = this.filter_settings.starttime || this.filter_settings.datetime;
                    return f.usage > 0;
                }
                else
                    return true;
            },
            IsAnyFilters: function () {
                if (this.ReadOnly) {
                    if (!this.column_filters) return false;

                    // If any filters are present that have more than a usage flag, return true
                    return Object.keys(this.column_filters).some(a => {
                        const f = this.column_filters[a];
                        if (!f) return false;

                        if ('usage' in f)
                            return f.usage > 0 && Object.keys(f).length > 1;
                        else
                            return true;
                    });
                }
                else
                    return this.column_filters && Object.keys(this.column_filters).length > 0;
            },
            IsAnyViewFilterChoices: function () {
                // Returns true if there are any filters available to the user in view or dashboard mode, whether they are being used by user or not.
                // Used to show or hide filter icon if there are no filters to choose from.
                return this.column_filter_list.filter(a => a.ColId != 'datetime' && a.ColId != 'starttime').length > 0;
            },
            TimeZone: function () {
                if (!this.timezone_expn && this.controlData.TimeZone) {
                    this.timezone_expn = utils.compile(this, this.controlData.TimeZone);
                }

                if (this.timezone_expn)
                    return utils.evaluate(this.timezone_expn, this);
                else
                    return '';
            },
            Filters: function () {
                if (!this.filters_expn && this.controlData.Filters) {
                    this.filters_expn = utils.compile(this, this.controlData.Filters);
                }

                if (this.filters_expn)
                    return utils.evaluate(this.filters_expn, this);
                else
                    return '';
            },
            StartDate: function () {
                return this.settings.startDate;
            },
            EndDate: function () {
                return this.settings.endDate;
            },
            SelectedRows: function () {
                return this.selectedRows
            },
            SelectedRowsAsArray: function () {
                return this.selectedRowsData;
            },
            SelectedRowCount: function () {
                if (this.selectedRows)
                    return this.selectedRows.length;

                return 0;
            },
            selectRowsEnabled: function () {
                if (this.selectRowsExpr) {
                    
                    const res = utils.evaluate(this.selectRowsExpr, this, false,
                        () => {
                            // This is a callback that will only be called if evaluate throws an exception.
                            // If this happens, we read the todisplayfield (so Vue knows we care) and then
                            // after 100ms, we update todisplayfailed to cause Vue to re-evaluate todisplay.
                            // (cap the retry at 10 times with increasing delay each attempt)
                            if (this.todisplayfailed >= 0 && this.todisplayfailed < 10)
                                setTimeout(() => this.todisplayfailed++, 50 * this.todisplayfailed);
                        });

                    return res;
                }
                else
                    return true;
            },
            agRowSelection: function () {
                return this.selectRowsEnabled == true ? this.rowSelectionConfig : null;
            },
            // Freezes the selection without changing it: isRowSelectable would deselect every checked row
            selectionLocked: function () {
                if (!this.selectionLockedExpr)
                    return false;

                return !!utils.evaluate(this.selectionLockedExpr, this, false);
            },
            style() {
                return {
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    // minHeight: "400px",
                    padding: this.controlData.IsDashboard ? '0 8px' : '8px',
                    ...this.sizeStyle,
                };
            },
            storageKey: function () {
                // In view mode, the storage key is based on the report name (which can't change during view-only mode)
                if (this.ReadOnly)
                    return `detailedHistory-${this.report_name}`;
                else
                    return `historyGrid-${this.parentType}-${this.controlData.Name || 'unnamed'}-${this.controlData.$objectid || 'noid'}`;
            },
            modelurlvalue: function () {
                if (this.modelurl)
                    return utils.evaluate(this.modelurl, this);
                else
                    return '';
            },
            modelrawvalue: function () {
                if (this.modelvalue)
                    return utils.evaluateObject(this.modelvalue, this);
                else
                    return [];
            },

            _initialState() {
                //const rawstate = this.System.LocalStorage(this.storageKey + '-state');
                let stateobj = {};
                //if (rawstate)
                //{
                //    stateobj = JSON.parse(rawstate);
                //    //console.log('Restoring state: ' + rawstate);

                //    if (stateobj.columnVisibility) {
                //        this.columnVisibility = stateobj.columnVisibility;

                //        //gridApi.refreshToolPanel();
                //    }
                //}

                if (this.column_state && this.column_state.columnOrder) {
                    const gridState = this.buildGridColumnState();
                    stateobj.columnOrder = gridState.columnOrder;
                    stateobj.columnVisibility = gridState.columnVisibility;
                }
              
                console.log('initialState:');
                console.log(JSON.stringify(stateobj, null, 3));

                return stateobj;
            },
            toolColumnVisibility: function () {
                return this.columnVisibility;
            },
            toolColumnDefs: function () {
                return this.columnDefs;
            },
            nocolumns: function () {
                if (this.columnDefs)
                    return !this.columnDefs.some(a => this.isReportDataColumn(a) && !a.hide);
                else
                    return true;
            },
            totalHits: function () {
                return this.total_rows == 10000 ? '10,000+' : this.total_rows.toLocaleString();
            },

            durationFormat: function () {
                return this.durationFormats[this.durationFormatIdx].value;
            },

            durationFormats: function () {
                return [
                    { text: 'HH:MM:SS', value: 'HH:MM:SS' },
                    { text: this.Translate('Minutes'), value: 'Minutes' },
                    { text: this.Translate('Seconds'), value: 'Seconds' },
                ];
            },

            sideBar: function () {
                return {
                    toolPanels: [
                        {
                            id: 'columns',
                            labelDefault: this.Translate('Columns'),
                            labelKey: 'columns',
                            iconKey: 'columns',
                            toolPanel: 'agColumnsToolPanel',
                            minWidth: 180,
                            maxWidth: 425,
                            width: 325
                        },
                        {
                            id: 'filters',
                            labelDefault: this.Translate('Filters'),
                            labelKey: 'filters',
                            iconKey: 'filter',
                            toolPanel: 'agFiltersToolPanel',
                            minWidth: 180,
                            maxWidth: 425,
                            width: 325
                        }
                    ],
                    hiddenByDefault: true,
                    position: 'right',
                    defaultToolPanel: null
                };
            },


            column_picker_list: function () {
                if (!this.ReadOnly)
                    return this.column_list;

                const order = this.column_state?.columnOrder
                    || this.viewonly_settings?.column_picker_list?.map(a => a.ColId)
                    || this.columnDefs.filter(a => a.colId).map(a => a.colId);

                return order
                    .map(colId => this.columnDefs.find(a => a.colId === colId))
                    .filter(Boolean)
                    .map(a => ({
                        ColId: a.colId,
                        Title: a.headerName,
                    }));
            },
            column_filter_list: function () {
                if (this.ReadOnly)
                    return this.viewonly_settings.column_filter_list;
                else
                    return this.column_list;
            },

            ReportModel: function () {
                let columns;
                if (this.column_state.columnOrder) {
                    // Persist selected (visible) columns in columnOrder; omit unchecked/hidden
                    columns = this.column_state.columnOrder
                        .filter(a => this.columnDefs.some(b => b.colId == a && this.isReportDataColumn(b) && !b.hide))
                        .map(colId => {
                            const col = { colId };
                            if (this.column_default_visibility[colId])
                                col.defaulthidden = true;
                            return col;
                        });

                    // If any visible columns aren't in the state order list, add them
                    for (let c of this.columnDefs.filter(a => this.isReportDataColumn(a) && !a.hide)) {
                        if (!columns.some(a => a.colId == c.colId))
                            columns.push({ colId: c.colId });
                    }
                }
                else
                    columns = this.columnDefs.filter(a => this.isReportDataColumn(a) && !a.hide).map(b => ({ colId: b.colId }));

                if (this.column_default_visibility)
                    for (let colid of Object.keys(this.column_default_visibility)) {
                        const c = columns.find(a => a.colId == colid);
                        if (c) c.defaulthidden = true;
                    }

                if (this.column_state.columnSizing)
                    for (let z of this.column_state.columnSizing) {
                        const c = columns.find(a => a.colId == z.colId);
                        if (c) c.width = z.width;
                    }

                if (this.column_state.leftPinned)
                    for (let z of this.column_state.leftPinned) {
                        const c = columns.find(a => a.colId == z);
                        if (c) c.pinLeft = true;
                    }

                if (this.column_state.rightPinned)
                    for (let z of this.column_state.rightPinned) {
                        const c = columns.find(a => a.colId == z);
                        if (c) c.pinRight = true;
                    }

                const colmods = [...new Set(columns.map(a => a.colId))].map(a => common.column_lookup[a]).flatMap(a => a && a.Dependencies ? a.Dependencies.Modules : []);
                const modules = [...new Set(colmods)];

                return {
                    source: this.source_selected,
                    filters: this.filter_settings || {},
                    thresholds: this.threshold_rules || [],
                    columns: columns,
                    dependencies: {
                        modules: modules
                    },
                };
            },
        },
        async mounted() {
            let hasSetTime = false;

            //const filters = this.QueryString('report_filters') || this.Filters;
            //if (filters) {
            //    const query_filters = JSON.parse(filters);
            //    for (let key of Object.keys(query_filters)) {
            //        if (key == 'starttime') {
            //            const filter = query_filters[key];

            //            this.settings.startDate = filter.filter;
            //            this.settings.endDate = filter.filterTo;

            //            // Update date range picker
            //            this.defaultTime = JSON.stringify({ startDate: this.settings.startDate, endDate: this.settings.endDate });
            //            // this.id++; // cause date range picker to reload
            //            hasSetTime = true;

            //            console.log(`-- mounted [${key}] filter:${JSON.stringify(filter)}`);
            //        }

            //        console.log(`-- mounted [${key}] defaultTime: ${this.defaultTime} - filters:${filters}`);
            //    }
            //}
            if (!hasSetTime) {
                //const oldDateRange = this.System.LocalStorage(this.storageKey + '-daterange');
                //if (oldDateRange) {
                //    //this.dateRange = oldDateRange;
                //    this.settings.startDate = oldDateRange.split(' - ')[0];
                //    this.settings.endDate = oldDateRange.split(' - ')[1];

                //    this.defaultTime = JSON.stringify({ startDate: this.settings.startDate, endDate: this.settings.endDate });
                //}
                //else {
                    this.settings.startDate = dayjs(common.getRelativeDateFromFilter('now/d')).format("YYYY-MM-DDTHH:mm:ss.SSS");
                    this.settings.endDate = dayjs(common.getRelativeDateFromFilter('now', true)).format("YYYY-MM-DDTHH:mm:ss.SSS");
                    
                    this.defaultTime = JSON.stringify({ startDate: this.settings.startDate, endDate: this.settings.endDate });
                //}

                utils.log(`-- mounted Loading localStorage(${this.storageKey}-daterange): ${this.settings.startDate} - ${this.settings.endDate}`);
            }

            this.autoGroupColumnDef = this.groupColDef_byMasterID;
        },
        methods: {
            generateThresholdStyle(thresholdColor)
            {
                switch(thresholdColor)
                {
                    case "#4AB574": //Green
                    {
                        return { color: thresholdColor, "background-color": "#D9F8E5", fontWeight:500 };
                    }
                    case "#E07A1A": //Orange
                    {
                        return { color: thresholdColor, "background-color": "#FCEDDF", fontWeight:500 };
                    }
                    case "#EF4A41": //Red
                    {
                        return { color: thresholdColor, "background-color": "#F8E4E3", fontWeight:500 };
                    }
                    default:
                    return { color: thresholdColor, fontWeight:500}
                }
            },
            rowSelectionChanged(e) {
                const rows = this.gridApi.getSelectedNodes();

                utils.log(`rowSelectionChanged - ${rows.length} row(s) selected`);

                this.selectedRowsData = rows.map(d => d.data);
                this.selectedRows = rows.map(d => d.data["_id"] ? d.data["_id"] : d.data.timelineid);

                // If a row class expression is present, tell the grid to redraw in case the selection criteria affects the row class
                if (this.controlData.RowClassExpression)
                    this.gridApi.redrawRows();
            },
            isDate(str) {
                if (typeof str !== 'string') {
                    return false;
                }
                const date = new Date(str);
                return !isNaN(date.valueOf());
            },
            extractDateFromDateMath(str){                              
                let currentDate = new Date(); 
                
                if (typeof str !== 'string' || !str) {
                    console.warn("Unable to extract date from date math string: " + str);
                    return ''; 
                }
                if (str === "now/" || str == "now")
                {
                    return new Date(currentDate.getTime());
                }

                const match = str.match(/now-(\d+)([dhsmwyM])/); 
                if (!match) {
                    console.warn("Unable to extract date from date math string: " + str);
                    return '';
                }

                const value = parseInt(match[1], 10);
                const unitIndicator = match[2]; 



      
                const newDate = new Date(currentDate.getTime());
                const operator = '-';

                // Apply the date math based on the unit and operator
                switch (unitIndicator) {
                    case 's': // seconds
                        newDate.setSeconds(newDate.getSeconds() + (operator === '-' ? -value : value));
                        break;
                    case 'm': // minutes
                        newDate.setMinutes(newDate.getMinutes() + (operator === '-' ? -value : value));
                        break;
                    case 'h': // hours
                        newDate.setHours(newDate.getHours() + (operator === '-' ? -value : value));
                        break;
                    case 'd': // days
                        newDate.setDate(newDate.getDate() + (operator === '-' ? -value : value));
                        break;
                    case 'w': // weeks (7 days)
                        newDate.setDate(newDate.getDate() + (operator === '-' ? -(value * 7) : (value * 7)));
                        break;
                    case 'M': // months (capital 'M' for months)
                        newDate.setMonth(newDate.getMonth() + (operator === '-' ? -value : value));
                        break;
                    case 'y': // years
                        newDate.setFullYear(newDate.getFullYear() + (operator === '-' ? -value : value));
                        break;
                    default:
                        // Handle unknown unit, though regex should prevent this if strict
                        return null;
                }
                return new Date(newDate);             
            },
            isValueMatch(operator, isValidValue, value, comparisonValue, isComparisonValueRelative, comparisonValueTo, isComparisonValueToRelative) {
                switch (operator) {
                    case "lessThan":  return isValidValue && (isComparisonValueRelative ? value > comparisonValue : value < comparisonValue);
                    case "lessThanOrEqualTo": return isValidValue && (isComparisonValueRelative ? value >= comparisonValue : value <= comparisonValue);
                    case "greaterThan": return isValidValue && (isComparisonValueRelative ? value < comparisonValue : value > comparisonValue);
                    case "greaterThanOrEqualTo": return isValidValue && (isComparisonValueRelative ? value <= comparisonValue : value >= comparisonValue);
                    case "equals": return value === comparisonValue;
                    case "notEquals": return value !== comparisonValue;
                    case "inRange": return isValidValue && ((value >= comparisonValue && value <= comparisonValueTo) || (value <= comparisonValue && value >= comparisonValueTo));
                    case "notInRange": return value <= comparisonValue || value >= comparisonValueTo;
                    case "missing": return !isValidValue;
                    case "contains": return isValidValue && value.includes(comparisonValue);
                    case "beginsWith": return isValidValue && value.startsWith(comparisonValue);
                    case "notContains": return !(isValidValue && value.includes(comparisonValue));
                    case "empty": return !isValidValue || value.length == 0;
                    case "notEmpty": return isValidValue && value.length > 0;
                    case "endsWith": return value.endsWith(comparisonValue);
                    default:
                        console.warn(`Unknown operator: ${operator}`);
                        return undefined; 
                }
            },
            selectThresholdStyle(threshold, params, isDateThreshold) {
                let value = Array.isArray(params.value) && threshold.type !== 'set' ? params.value.join(' ') : params.value;
                let comparisonValue = threshold.value;
                let comparisonValueTo = threshold.valueTo;
                let isComparisonValueRelative = false;
                let isComparisonValueToRelative = false;

                if (isDateThreshold)
                {
                    // Absence has to be decided before the coercion below. DateTime.MinValue
                    // survives new Date(...).getTime() as a real timestamp smaller than everything,
                    // so every "less than" rule matches a cell the value formatter renders blank.
                    // Only the operators that explicitly test for absence may match a blank date.
                    if (common.isBlankDateValue(value))
                    {
                        return (threshold.operator === 'missing' || threshold.operator === 'empty')
                            ? this.generateThresholdStyle(threshold.color)
                            : null;
                    }

                    if (!this.isDate(comparisonValue))
                    {
                        comparisonValue = this.extractDateFromDateMath(comparisonValue);
                        isComparisonValueRelative = true;
                    }
                    if (!this.isDate(comparisonValueTo))
                    {
                        comparisonValueTo = this.extractDateFromDateMath(comparisonValueTo);
                        isComparisonValueToRelative = true;
                    }
                    value = new Date(value).getTime();
                    comparisonValue =  new Date(comparisonValue).getTime();
                    comparisonValueTo =  new Date(comparisonValueTo).getTime();
                }
                
                let isValidValue = value !== undefined && value !== null;
                let operator = threshold.operator || 'equals';
                
                let isMatched = this.isValueMatch(operator, isValidValue, value, comparisonValue, isComparisonValueRelative, comparisonValueTo, isComparisonValueToRelative);
                if (isMatched) {
                    return this.generateThresholdStyle(threshold.color);
                } else if (isMatched === undefined) {
                    console.warn(`Detected missing duration threshold logic for operator type: ${operator} ${JSON.stringify(threshold)}, v: ${value}, cv: ${comparisonValue},  cvt: ${comparisonValueTo}, icvr: ${isComparisonValueRelative}`);
                }

                return null; 
            },
            async preRenderComplete() {
                this.finishRenderHandler(this);

                // Look for a form_changed_indicator up the parent hierarchy
                let p = this.$parent;
                while (p && (!('cc_form_changed_indicator' in p) || !p.cc_form_changed_active))
                    p = p.$parent;

                if (p) {
                    p.cc_form_changed_linked = true;
                    p.cc_form_changed_form = this;
                    this.notify_object = p;
                }

                await common.loadLookupTables(this._uid, this.ReadOnly);

                this.sourceTypes = common.sourceTypesList;

                await this.loadReportModel();

                if (this.report_model && this.report_model.source)
                    this.source_selected = this.report_model.source;

                this.loadColumns(this.source_selected);

                if (this.report_model)
                    this.applyReportModel();

                this.colDefsLoaded = true;
                this.visibleColumnIds = this.columnDefs.filter(a => this.isReportDataColumn(a) && !a.hide).map(a => a.colId);

                const delay = this.delay_expn ? parseInt(utils.evaluate(this.delay_expn, this)) || 0 : 0;
                if (delay)
                    this.interval = setInterval(() => {
                        this.Refresh();
                    }, delay * 60 * 1000);
                this.report_model.report_is_loaded = true;
                //this.Refresh();
            },
            async loadReportModel() {
                console.log('loadReportModel(); controlData:');
                console.log(JSON.stringify(this.controlData));

                let model;
                if (this.controlData.DataType == 'URL' && this.modelurl) {
                    try {
                        model = this.modelurlvalue ? await utils.api.get(this.modelurlvalue, false, false) : [];
                    }
                    catch (e) {

                        // SignalR backwards compatability
                        var statusCode = e.StatusCode ? e.StatusCode : e.statusCode;
                        var reasonPhrase = e.ReasonPhrase ? e.ReasonPhrase : e.reasonPhrase;

                        let msg = `Failed in API call to ${this.modelurlvalue}`;
                        utils.warn(`${this.type} ${this.name} ${msg}`, e);
                        msg = `${this.Translate('API call failed')}: ${reasonPhrase}`;
                        this.EventBus.$emit('v-toast', {
                            text: this.Translate('Error'),
                            subtext: e ? msg : this.Translate('Unknown error'),
                            type: 'error',
                            icon: 'mdi mdi-alert',
                            ontap: [
                                {
                                    "PrimitiveAction": true,
                                    "ActionType": "DisplayConfirmModal",
                                    "RunAsync": false,
                                    "ActionData": {
                                        "OkayButtonText": this.Translate("Okay"),
                                        "CancelButtonText": this.Translate("Cancel"),
                                        "Debug": {},
                                        "Title": this.Translate('Error') + " " + (e ? statusCode : ''),
                                        "Message": `Failed in API call to ${this.modelurlvalue} - ${reasonPhrase}`
                                    }
                                }
                            ],
                            context: this,
                        });
                    }
                }
                else if (this.controlData.DataType == 'RawInterpolated' && this.modelvalue)
                    try {
                        model = this.modelrawvalue;

                        console.log(`model: ${JSON.stringify(model)}`);
                    }
                    catch (e) {
                        utils.warn('BasicGrid modelvalue ' + this.controlData.Data + ' failed to evaluate: ' + e);
                        model = {};
                    }
                else if (this.controlData.DataType == 'Raw' && this.controlData.Data)
                    model = this.controlData.Data;

                this.report_model = model;
            },
            applyReportModel() {
                // First filter out any filters or columns that do not exist (may have been deleted)
                if (this.report_model.filters)
                    this.report_model.filters = helps.toLookup(Object.keys(this.report_model.filters).filter(a => a in common.column_lookup).map(a => ({ colId: a, value: this.report_model.filters[a] })), 'colId', 'value');
                if (this.report_model.columns)
                    this.report_model.columns = this.report_model.columns.filter(a => a.colId in common.column_lookup);

                    if (this.report_model.source)
                    this.source_selected = this.report_model.source;

                    if (this.CategoryID)
                    this.source_selected = this.CategoryID;

                if (this.report_model.filters) {
                    var existingFilters = this.filter_settings;
                    this.filter_settings = this.report_model.filters;
                    if(!this.filter_settings.extralegtype && existingFilters.extralegtype){
                          this.filter_settings.extralegtype = existingFilters.extralegtype;
                    }

                    this.column_filters = { ...this.report_model.filters }; // Used only to pre-initialize filters pane
                }

                if (this.report_model.thresholds) {
                    this.threshold_rules = this.report_model.thresholds;
                }


                if (this.report_model.columns) {
                    // Make visible all columns listed in the report definition
                    for (let c of this.report_model.columns) {
                        const cdef = this.columnDefs.find(a => a.colId == c.colId);
                        if (cdef && this.isReportDataColumn(cdef)) {
                            cdef.hide = false;

                            if (c.width)
                                cdef.width = c.width;
                            if (c.pinLeft)
                                cdef.pinned = 'left';
                            else if (c.pinRight)
                                cdef.pinned = 'right';

                            if (c.defaulthidden)
                                this.column_default_visibility[c.colId] = 1;
                        }
                        else if (!cdef)
                            console.log(`Failed to locate col:${c.colId}`);
                    }

                    const colvisjson = this.System.LocalStorage(this.storageKey + '-columnsvisible');
                    let colhide;
                    if (colvisjson) {
                        colhide = JSON.parse(colvisjson).filter(a => !a.visible).map(a => a.colId);

                        this.column_default_visibility = {};
                        for (let colid of colhide)
                            this.column_default_visibility[colid] = 1;
                    }

                    this.column_state = {
                        columnOrder: this.report_model.columns.map(a => a.colId).filter(id => this.isReportDataColumn(this.columnDefs.find(c => c.colId == id))),
                        columnSizing: this.report_model.columns.filter(a => a.width).map(a => ({ colId: a.colId, width: a.width })),
                        leftPinned: this.report_model.columns.filter(a => a.pinLeft).map(a => a.colId),
                        rightPinned: this.report_model.columns.filter(a => a.pinRight).map(a => a.colId),
                    };

                    let stateobj = {};

                    if (this.ReadOnly) {
                        this.viewonly_settings = {
                            column_picker_list: this.column_list.filter(a => this.column_state.columnOrder.includes(a.ColId)),
                            column_filter_list: this.column_list.filter(a => this.report_model.filters[a.ColId] && this.report_model.filters[a.ColId].usage),
                        };

                        if (this.column_state && this.column_state.columnOrder) {
                            let hiddencols;
                            if (colhide)
                                hiddencols = this.columnDefs.filter(a => a.colId && (a.hide || colhide.includes(a.colId))).map(a => a.colId);
                            else
                                hiddencols = this.columnDefs.filter(a => a.colId && (a.hide || this.column_default_visibility[a.colId])).map(a => a.colId);

                            for (let col of this.columnDefs)
                                if (this.column_default_visibility[col.colId])
                                    col.hide = true;

                            const gridState = this.buildGridColumnState(hiddencols);
                            stateobj.columnOrder = gridState.columnOrder;
                            stateobj.columnVisibility = gridState.columnVisibility;
                        }

                        this.initial_column_filters = _.cloneDeep(this.report_model.filters); // Used in ReadOnly to provide the base filter (for limited filtering, usage:2)

                        const filters = this.QueryString('report_filters') || this.Filters;
                        if (filters) {
                            const query_filters = JSON.parse(filters);
                            for (let key of Object.keys(query_filters)) {
                                //if (key in this.initial_column_filters && this.initial_column_filters[key].usage)
                                {
                                    // Only allow filters that have been allowed
                                    const filter = query_filters[key];

                                    // Merge together the default filter with the query string filter
                                    //this.initial_column_filters[key] = { ...this.initial_column_filters[key], ...filter };
                                    this.report_model.filters[key] = { ...this.report_model.filters[key], ...filter };
                                    this.column_filters[key] = { ...this.column_filters[key], ...filter };

                                    //if (key == 'starttime') {
                                    //    this.settings.startDate = filter.filter;
                                    //    this.settings.endDate = filter.filterTo;

                                    //    // Update date range picker
                                    //    this.defaultTime = JSON.stringify({ startDate: this.settings.startDate, endDate: this.settings.endDate });
                                    //    this.id++; // cause date range picker to reload
                                    //}

                                    console.log(`-- applyReportModel this.column_filters[${key}]: ${JSON.stringify(this.column_filters[key], null, 3)}`);
                                }
                            }
                        }

                        if ('starttime' in this.column_filters || 'datetime' in this.column_filters) {
                            const filter = this.column_filters.starttime || this.column_filters.datetime;
                            this.settings.startDate = common.getRelativeDateFromFilter(filter.filter);
                            this.settings.endDate = common.getRelativeDateFromFilter(filter.filterTo, true);
                            this.defaultTime = JSON.stringify({ startDate: this.settings.startDate, endDate: this.settings.endDate });

                            console.log(`-- applyReportModel this.column_filters.starttime: ${JSON.stringify(filter, null, 3)}`);
                            console.log(`-- applyReportModel defaultTime: ${this.defaultTime}`);
                        }
                    }
                    else {
                        if (this.column_state && this.column_state.columnOrder) {
                            const gridState = this.buildGridColumnState();
                            stateobj.columnOrder = gridState.columnOrder;
                            stateobj.columnVisibility = gridState.columnVisibility;
                        }
                    }
                    console.log('setting initialState:');
                    console.log(JSON.stringify(stateobj));

                    this.initialState = stateobj;

                    console.log('Initial Column Defs:');
                    // Only log plain fields — IconComponent col defs carry `context: this` (circular / Window)
                    console.log(JSON.stringify(
                        this.columnDefs
                            .filter(a => a.colId && !a.hide)
                            .map(a => ({ colId: a.colId, field: a.field, headerName: a.headerName, hide: a.hide, pinned: a.pinned })),
                        null,
                        3
                    ));

                    console.log('column_state.columnOrder:');
                    console.log(JSON.stringify(this.column_state.columnOrder));
                }

                else if (this.gridApi)
                    this.Refresh();
                else
                    this.refreshOnReady = true;
            },
            modelChanged() {
                // If the form model changes set the cc_form_changed_indicator
                if (this.notify_object) {
                    utils.warn(`AdvancedHistory ${this.name} change fired, notifying ${this.notify_object ? this.notify_object.name : 'null'}`);
                    //utils.warn(JSON.stringify(newvalue));

                    this.notify_object.formChanged();
                }
            },
            async Refresh() {
                if (this.query_in_progress) {
                    this.query_refresh_pending = true;
                    return;
                }

                if (this.gridApi && this.settings.startDate && this.settings.endDate) {
                    this.after_key = null;
                    this.gridApi.refreshServerSide();
                }
            },

            async precompileIconActions() {
                // Compile the actions for each Icon to allow interpolation within each row efficiently
                for (let item of this.controlData.Icons) {
                    if (item.DisplayExpression)
                        item.displayExpn = utils.compileExpression(this, item.DisplayExpression, 'row');
                    if (item.VisibleExpression)
                        item.visibleExpn = utils.compileExpression(this, item.VisibleExpression, 'row');
                    if (item.EnabledExpression)
                        item.enabledExpn = utils.compileExpression(this, item.EnabledExpression, 'row');

                    if (item.Actions)
                        for (let act of item.Actions) {
                            await utils.compileAction(this, act);
                        }

                    if (item.MenuItems)
                        for (let m of item.MenuItems) {
                            if (m.Title)
                                m.titleExpn = utils.compile(this, m.Title, null, 'row');
                            if (m.Tooltip)
                                m.tooltipExpn = utils.compile(this, m.Tooltip, null, 'row');
                            if (m.Icon)
                                m.iconExpn = utils.compile(this, m.Icon, null, 'row');

                            if (m.VisibleExpression)
                                m.visibleExpn = utils.compileExpression(this, m.VisibleExpression, 'row');
                            if (m.EnabledExpression)
                                m.enabledExpn = utils.compileExpression(this, m.EnabledExpression, 'row');

                            if (m.Actions)
                                for (let act of m.Actions) {
                                    await utils.compileAction(this, act);
                                }
                        }
                }
            },

            async loadSourceTypes() {
                const res = await utils.api.get('Apps/Platform/Schema/AdvancedReports/v1/ReportingSources');
                try {
                    return res.SourceTypes.toSorted((a, b) => common.byField(a, b, 'DisplayName'));
                }
                catch (ex) {
                    console.error(ex.message);
                    return [];
                }
            },
            async loadCustomerList() {
                if (this.GlobalVars && this.GlobalVars.AllCustomers)
                    return common.remaplookup(this.GlobalVars.AllCustomers, 'Name');
                else {
                    const res = await utils.api.get('/Apps/ReportStudio/Utils/GetMyCustomerList');
                    return common.transformer(res, 'Name', 'CustomerID');
                }
            },
            async loadUserList() {
                if (this.GlobalVars && this.GlobalVars.AllUsers)
                    return common.remaplookup(this.GlobalVars.AllUsers, 'FullName');
                else if (this.GlobalVars && this.GlobalVars.Users)
                    return common.remaplookup(this.GlobalVars.Users, 'fullname');
                else {
                    const res = await utils.api.get('/Apps/ReportStudio/Utils/GetMyUserList');
                    return common.transformer(res, 'FullName', 'UserID');
                }
            },
            async loadChatTouchpoints() {
                const res = await utils.api.get('/Apps/Common/ChatTouchPoints');
                return common.transformer(res, 'Name', 'TouchPointID');
            },
            loadColumns(source, reassign) {
                this.column_list = common.column_list.filter(a => a.Sources.includes(source));

                const visible = this.columnDefs.filter(a => a.colId && !a.hide).map(a => a.colId);

                this.columnDefs = [
                    {
                        colId: 'ah-icons',
                        // Utility grid chrome — not a user-selectable report column (avoids empty Selected columns pill)
                        suppressColumnsToolPanel: true,
                        lockPosition: 'left',
                        pinned: 'left',
                        sortable: false,
                        suppressMovable: true,
                        filter: false,
                        suppressHeaderMenuButton: true,
                        cellRenderer: 'IconComponent',
                        cellRendererParams: {
                            customdata: {
                                root: this.root,
                                scopeitems: this.scopeitems,
                                controlscope: this.controlscope,
                                context: this,
                                Icons: this.controlData.Icons,
                            }
                        },
                    },
                    ...common.getColumnDefs(source, this.applyThresholdStyling)
                ];

                //if (this.selectRowsEnabled) {
                //    this.columnDefs.unshift({
                //        headerName: '',
                //        sortable: false,
                //        resizable: true,
                //        checkboxSelection: true,
                //        headerCheckboxSelection: false,
                //        headerCheckboxSelectionFilteredOnly: false,
                //        showDisabledCheckboxes: false,
                //        pinned: 'left',
                //        lockPosition: 'left',
                //        width: 25,
                //    });
                //}

                for (let cdef of this.columnDefs)
                    if (visible.includes(cdef.colId))
                        cdef.hide = false;

                if (this.gridApi && reassign)
                    this.gridApi.setGridOption('columnDefs', this.columnDefs);

                if (this.ReadOnly)
                    this.viewonly_settings = {
                        column_picker_list: [],
                        column_filter_list: [],
                    };

                this.column_default_visibility = {};
                if (this.columnDefs.some(a => a.colId == 'datetime'))
                    this.filter_settings = {
                        datetime: {
                            filterType: 'number',
                            type: 'inRange',
                            filter: 'now/d',
                            filterTo: 'now',
                            usage: 1
                        },
                        extralegtype: {
                            filterType: 'set',
                            values: ['Agent'],
                            mode: 'exclusive',
                            usage: 0
                        },
                    };
                else
                    this.filter_settings = {};

                this.column_filters = { ...this.filter_settings };

                // Force components with the unique_id as the key to reload
                if (reassign) {
                    this.unique_id = utils.generateUUID();
                    this.Refresh();
                }
            },
            applyThresholdStyling(params, column)
            {
                const isRightAligned = ['durationColumn', 'countColumn', 'floatColumn', 'percentColumn', 'currencyColumn'].includes(params.colDef.type);
                const getBaseStyle = () => {
                    if (isRightAligned) {
                        return { textAlign: 'right' };
                    }
                    return { textAlign: 'left' };
                };

                var selectedStyle = null;
                if (this.threshold_rules)
                {
                    for (let i = this.threshold_rules.length - 1; i >= 0; i--) {
                        
                        const threshold = this.threshold_rules[i];
                        if (threshold.id === column.ColId)
                        {
                            switch(threshold.type)
                            {
                                case "set":
                                {
                                    let possibleSelectionIds = threshold.value;
                                    let columnLookups = {};
                                    if (column.lookups)
                                    {
                                        columnLookups = column.lookups;
                                    }
                                    let possibleSelections = possibleSelectionIds.map(key => columnLookups[key]);
                                    let selectedValue = params.value; 
                                    let found = Array.isArray(selectedValue) 
                                                            ? selectedValue.some(s=> possibleSelectionIds.includes(s) || possibleSelections.includes(s)) 
                                                            : possibleSelectionIds.includes(selectedValue) || possibleSelections.includes(selectedValue);
                                    if (found)
                                    {
                                        var updatedStyle = this.generateThresholdStyle(threshold.color);
                                        if (updatedStyle)
                                        {
                                            selectedStyle = updatedStyle;
                                            Object.assign(selectedStyle, getBaseStyle());
                                        }   
                                    }                              
                                    break;
                                }
                                case "date":
                                {
                                    var updatedStyle = this.selectThresholdStyle(threshold, params, true);       
                                    if (updatedStyle)
                                    {
                                        selectedStyle = updatedStyle;
                                        Object.assign(selectedStyle, getBaseStyle());
                                    }                        
                                    break;
                                }
                                case "boolean":
                                {
                                    const normalizeThresholdBoolean = (v) => {
                                        if (v === true || v === 'true' || v === 'True') return 'True';
                                        if (v === false || v === 'false' || v === 'False') return 'False';
                                        return null;
                                    };
                                    const toBooleanCellLabel = (v) => String(v).toLowerCase() === 'true' ? 'True' : 'False';
                                    let possibleSelectionIds = Array.isArray(threshold.value)
                                        ? threshold.value
                                        : (threshold.value != null ? [threshold.value] : []);
                                    possibleSelectionIds = possibleSelectionIds
                                        .map(normalizeThresholdBoolean)
                                        .filter(v => v != null);
                                    const selectedValue = toBooleanCellLabel(params.value);
                                    if (possibleSelectionIds.includes(selectedValue))
                                    {
                                        var updatedStyle = this.generateThresholdStyle(threshold.color);
                                        if (updatedStyle)
                                        {
                                            selectedStyle = updatedStyle;
                                            Object.assign(selectedStyle, getBaseStyle());
                                        }
                                    }
                                    break;
                                }
                                case "direction":
                                {
                                    let possibleSelectionIds = Array.isArray(threshold.value)
                                        ? threshold.value
                                        : (threshold.value != null ? [threshold.value] : []);
                                    const normalizeDirection = (v) => (typeof v === 'string' ? v.toLowerCase() : v);
                                    let selectedValue = normalizeDirection(params.value);
                                    possibleSelectionIds = possibleSelectionIds.map(normalizeDirection);
                                    let columnLookups = column.lookups || {};
                                    let possibleSelections = possibleSelectionIds.map(key => columnLookups[key]);
                                    let found = Array.isArray(selectedValue)
                                        ? selectedValue.some(s => possibleSelectionIds.includes(s) || possibleSelections.includes(s))
                                        : possibleSelectionIds.includes(selectedValue) || possibleSelections.includes(selectedValue);
                                    if (found)
                                    {
                                        var updatedStyle = this.generateThresholdStyle(threshold.color);
                                        if (updatedStyle)
                                        {
                                            selectedStyle = updatedStyle;
                                            Object.assign(selectedStyle, getBaseStyle());
                                        }
                                    }
                                    break;
                                }
                                case "duration":
                                case "number":
                                case "percent":
                                {
                                    var updatedStyle = this.selectThresholdStyle(threshold, params, false);       
                                    if (updatedStyle)
                                    {
                                        selectedStyle = updatedStyle;
                                        Object.assign(selectedStyle, getBaseStyle());
                                    }    

                                    break;
                                }
                                case "searchableText":
                                {
                                    var updatedStyle = this.selectThresholdStyle(threshold, params, false);       
                                    if (updatedStyle)
                                    {
                                        selectedStyle = updatedStyle;
                                        Object.assign(selectedStyle, getBaseStyle());
                                    }    
                                    break;
                                }                    
                                default:
                                {
                                    console.log("We are in a different type of field!");
                                    console.log(threshold.type);
                                    break;
                                }
                            }
                        }
                    }     
                }               
                return selectedStyle || getBaseStyle();
            },
            async loadColumns_old(source, reassign) {
                // Grab the sets of columns by usage
                const columns = await utils.api.get('Apps/Platform/Schema/AdvancedReports/v1/Column/ListByID?OnlyMine=true');
                common.column_lookup = helps.toLookup(columns, 'ColId');

                this.column_list = columns.filter(a => a.Sources.includes(source));

                console.log(`loadColumns(source:${source} returned ${this.column_list.length} column(s)`);

                try {
                    const tasks = [];
                    for (let c of this.column_list) {
                        if (c.MappingTableType == 'LookupAPI') {
                            tasks.push(common.loadLookup(c));
                        }
                        else if (c.MappingTableType == 'LookupList') {
                            c.lookups = helps.toLookup(c.MappingTable, 'Key', 'Value');
                        }
                    }
                    await Promise.all(tasks);

                    console.log(`loadColumns completed lookups for ${tasks.length} task(s)`);
                }
                catch (err) {
                    console.error(`loadColumns - loading lookups failed: ${err.message || err.reasonPhrase}`);
                }

                // Generate the list of column definitions for ag-grid
                let cols = this.column_list.filter(a => a.SourceType).map(a => ({
                    colId: a.ColId,
                    field: a.ColumnUse.Computed ? a.ColId : a.SourceField.replace('.keyword',''), // Strip any fields with the .keyword suffix as this will not match the column in the result set
                    headerName: this.Translate(a.Title),
                    type: a.SourceType,
                    hide: true,
                    menuTabs: ['generalMenuTab'],
                })).toSorted((a, b) => common.byField(a, b, 'headerName'));

                for (let i = 0; i < cols.length; i++) {
                    const row = cols[i];
                    if (row.type == 'textSetColumn' || row.type == 'phoneNumColumn') {
                        row.filterParams = {
                            values: this.getUniqueColValues(row.colId),
                            refreshValuesOnOpen: true
                        };
                        const c = common.column_lookup[row.colId];
                        if (c && c.lookups) {
                            row.valueFormatter = (v) => {
                                if (typeof v.value === 'object' && Array.isArray(v.value))
                                    return v.value.map(a => c.lookups[a] || a).join(', ');
                                else
                                    return c.lookups[v.value] || v.value;
                            }
                        }
                    }
                }

                this.columnDefs = [
                    {
                        //checkboxSelection: true,
                        //showDisabledCheckboxes: true,
                        colId: 'ah-icons',
                        // Utility grid chrome — not a user-selectable report column (avoids empty Selected columns pill)
                        suppressColumnsToolPanel: true,
                        lockPosition: 'left',
                        pinned: 'left',
                        sortable: false,
                        suppressMovable: true,
                        filter: false,
                        suppressHeaderMenuButton: true,
                        cellRenderer: 'IconComponent',
                        cellRendererParams: {
                            customdata: {
                                root: this.root,
                                scopeitems: this.scopeitems,
                                controlscope: this.controlscope,
                                context: this,
                                Icons: this.controlData.Icons,
                            }
                        },
                    },
                    ...cols
                ];

                console.log(`loadColumns - assigned ${this.columnDefs.length} column def(s)`);

                if (this.gridApi && reassign)
                    this.gridApi.setGridOption('columnDefs', this.columnDefs);

                // Force components with the unique_id as the key to reload
                if (reassign) {
                    console.log(`loadColumns reassigning unique_id: ${this.unique_id} (_uid:${this._uid})`);

                    this.unique_id = utils.generateUUID();

                    console.log(`loadColumns reassigned unique_id: ${this.unique_id} (_uid:${this._uid})`);
                }
            },
            async loadLookupTables() {
                if (this.report_model && this.report_model.source)
                    this.source_selected = this.report_model.source;

                const [sourcetypes, customerlist, userlist, chatlist, _] = await Promise.all([
                    this.loadSourceTypes(),
                    this.loadCustomerList(),
                    this.loadUserList(),
                    this.loadChatTouchpoints(),
                    this.loadColumns(this.source_selected)
                ]);

                common.sourceTypes = helps.toLookup(sourcetypes, 'SourceName', 'DisplayName');
                this.sourceTypes = sourcetypes;
                common.customerLookup = customerlist;
                common.userLookup = userlist;
                common.chatLookup = chatlist;
            },

            notifyfilterschanged(filters, colId) {
                console.log('notifyfilterschanged:');
                console.log(JSON.stringify(filters, null, 3));

                if ((colId == 'starttime' || colId == 'datetime') && colId in filters) {
                    const f = filters[colId];
                    if (f.usage > 0) {
                        this.settings.startDate = common.getRelativeDateFromFilter(f.filter);
                        this.settings.endDate = common.getRelativeDateFromFilter(f.filterTo, true);
                        this.defaultTime = JSON.stringify({ startDate: this.settings.startDate, endDate: this.settings.endDate });
                    
                        this.useStartEnd = false;
                    }
                }

                this.filter_settings = filters;
                this.Refresh();

                this.modelChanged();
            },
            notifyThresholdsChanged(thresholds) {
                this.threshold_rules = thresholds;
                if (this.report_model)
                    this.report_model.thresholds = this.threshold_rules; 
                this.Refresh();
                this.modelChanged();               
            },
            async queryFlatTable(params) {
                // When using rowGroup:
                // {"startRow":0,"endRow":100,"rowGroupCols":[{"id":"direction","displayName":"Direction","field":"direction"}],"valueCols":[],"pivotCols":[],"pivotMode":false,"groupKeys":["inbound"],"filterModel":{},"sortModel":[]}
                const q_from = this.using_aggs ? 0 : params.startRow;
                const q_size = this.using_aggs ? this.pageSize : (params.endRow - params.startRow);
                let dataurl = `${common.buildRequest(this.baseurl, this.settings.startDate, this.settings.endDate)}&PageSize=${q_size}&From=${q_from}&Source=${this.source_selected}`;

                if (this.columnDefs && this.columnDefs.length > 0) {
                    let cols;
                    // In view mode, query for all columns in the report
                    if (this.ReadOnly)
                        cols = this.column_picker_list.map(a => a.ColId);
                    // In edit mode, query for the columns we are displaying
                    else
                        cols = this.columnDefs.filter(a => a.colId && !a.hide).map(a => a.colId);

                    if (cols.length < 1) {
                        console.log('No columns selected');
                        return {
                            rowData: [],
                            rowCount: 0,
                        };
                    }

                    // The drill-down ability depends on these columns, so even though we might not be displaying them, we must request them.
                    if (!cols.includes('timelineid'))
                        cols.push('timelineid');
                    if (!cols.includes('timelinetype'))
                        cols.push('timelinetype');
                    if (!cols.includes('recordtype'))
                        cols.push('recordtype');
                    if (!cols.includes('queuename'))
                        cols.push('queuename');
                    if (!cols.includes('recorded'))
                        cols.push('recorded');
                    if (!cols.includes('recordingexpire'))
                        cols.push('recordingexpire');
                    // This isn't very maintainable to have to include destination this way, but the drill-down for call CDR depends on it...
                    if (this.source_selected == 'contacts' && !cols.includes('destination'))
                        cols.push('destination');

                    dataurl += `&Columns=${encodeURIComponent(JSON.stringify(cols))}`;
                }

                if (params.sortModel && params.sortModel.length) {
                    dataurl += `&sortModel=${JSON.stringify(params.sortModel)}`;
                }

                if (params.filterModel) {
                    // Mix the ag-grid filter settings with the newer custom filter settings from agGridToolReportFilters.vue
                    const f = { ...this.filter_settings, ...params.filterModel };
                    dataurl += '&filters=' + encodeURIComponent(JSON.stringify(f));
                    console.log('filterModel: ' + JSON.stringify(f));
                }

                if (this.search)
                    dataurl += '&search=' + encodeURIComponent(this.search);

                if (this.TimeZone)
                    dataurl += '&WindowsTimeZone=' + encodeURIComponent(this.TimeZone);

                if (this.report_name)
                    dataurl += '&referenceid=' + encodeURIComponent(this.report_name);

                if (this.useStartEnd)
                    dataurl += '&UseStartEnd=true';

                // If we want to override the call center's time zone, we can use this to get our browser's time zone:
                //  Intl.DateTimeFormat().resolvedOptions().timeZone

                console.log('url: ' + dataurl.split('&').join('\n'));

                this.query_in_progress = true;
                try {
                    const res = await utils.api.get(dataurl);

                    console.log(`get returned ${res && res.rows ? res.rows.length : 0} row(s)`);
                    console.log(JSON.stringify(res.rows.slice(0,3), null, 3));

                    this.query_duration = res.took;
                    this.total_rows = res.total;
                    this.using_aggs = res.aggs;
                    this.timezone = res.timezone;

                    return {
                        rowData: res.rows,
                        rowCount: this.total_rows,
                    };
                }
                catch (ex) {
                    console.log(`get failed ${JSON.stringify(ex)}`);

                    this.queryError = ex.message || ex.reasonPhrase;

                    return {
                        rowData: [],
                        rowCount: 0,
                    };
                }
                finally {
                    this.query_in_progress = false;

                    // Check for any requests to refresh that were issued while a query was still in progress.
                    // Trigger a delayed refresh
                    if (this.query_refresh_pending) {
                        this.query_refresh_pending = false;
                        const thisref = this;
                        setTimeout(() => {
                            if (!thisref.query_in_progress)
                                thisref.Refresh();
                        }, 250);
                    }
                }
            },

            getColumnVisibility() {
                return this.columnVisibility;
            },
            getColumnDefs() {
                return this.columnDefs;
            },
            // User-facing report columns only — excludes grid chrome like ah-icons / selection
            isReportDataColumn(col) {
                return !!(col?.colId && !col.suppressColumnsToolPanel && !this.leadingPinnedColIds.includes(col.colId));
            },

            customerLookupFormatter(v) {
                // v.value is the cell's content
                // v.data is the row data
                return common.customerLookup[v.value] || v.value;
            },
            userLookupFormatter(v) {
                let value;
                if (typeof v.value === 'object' && Array.isArray(v.value))
                    value = v.value.map(a => common.userLookup[a] || a).join(', ');
                else
                    value = common.userLookup[v.value] || v.value;

                //console.log(`customerLookupFormatter(v:${JSON.stringify(v.value)}): ${value}`);
                return value;
            },
            phoneNumLookupFormatter(v) {
                // note: v.data is the full row record
                switch (v.data.timelinetype) {
                    case 'Chat':
                        return common.chatLookup[v.value] || v.value;

                    case 'EMail':
                        return v.value;

                    case 'SMS':
                    case 'Fax':
                    case 'Call':
                    default:
                        if (v.value && v.value.substring(0, 4) == 'USER')
                            return common.userLookup[v.value.substring(4)] || v.value;
                        else if (v.value && v.value.length == 36)
                            return common.userLookup[v.value] || v.value;
                        else if (v.value == '+10000000000')
                            return '(Loopback)';
                        else
                            return helps.formatPhoneNumber(v.value);
                }
            },

            customerLookupComparator(a, b) {
                const ca = common.customerLookup[a] || a;
                const cb = common.customerLookup[b] || b;
                return ca > cb ? 1 : ca < cb ? -1 : 0;
            },
            userLookupComparator(a, b) {
                const ca = common.userLookup[a] || a;
                const cb = common.userLookup[b] || b;
                return ca > cb ? 1 : ca < cb ? -1 : 0;
            },
            phoneNumLookupComparator(a, b) {
                const ca = this.phoneNumLookupFormatter(a);
                const cb = this.phoneNumLookupFormatter(b);
                return ca > cb ? 1 : ca < cb ? -1 : 0;
            },

            getUniqueColValues(colname) {
                return async (params) => {
                    const dataurl = `${common.buildRequest('Apps/ReportStudio/AdvancedReporting/GetUniqueColValues', this.settings.startDate, this.settings.endDate)}&Column=${colname}`;

                    console.log(`getUniqueColValues (${colname}) url: ` + dataurl.split('&').join('\n'));

                    const res = await utils.api.get(dataurl);
                    params.success(res.Values);
                };
            },

            notifyCustomMetrics(cols, dsl) {
                //alert('notify custom metrics ' + JSON.stringify(cols) + '  -  ' + JSON.stringify(dsl));
                this.customMetrics = dsl;

                const colDefs = [
                    ...this.columnDefs.map(c => ({ colId: c.colId, field: c.field, headerName: c.headerName, type: c.type })),
                    //{ "colId": "count", "field": "count", "filter": false, "headerName": "Count", "hide": true, type: "countColumn" },
                    ...cols
                ];
                this.gridApi.setGridOption('columnDefs', colDefs);
            },

            byField(a, b, field) {
                return a[field] < b[field] ? -1 : a[field] > b[field] ? 1 : 0;
            },

            remove_count(field)
            {
                if (field.length > 6 && field.substring(field.length-6) == '_count')
                    return field.substring(0, field.length-6);
                else
                    return '__ignore_me__';
            },

            onGridReady(params) {
                this.gridApi = params.api;
                this.columnApi = params.columnApi;
                this.ensureLeadingPinnedColumns();
            },
            ensureLeadingPinnedColumns() {
                if (!this.gridApi)
                    return;

                const leading = this.leadingPinnedColIds.filter(id => !!this.gridApi.getColumn(id));
                if (!leading.length)
                    return;

                // initialState columnOrder often omits selection/icon cols and can leave the
                // checkbox in the center region; force them to the front of the left pin group.
                this.gridApi.applyColumnState({
                    state: leading.map(colId => ({
                        colId,
                        pinned: 'left',
                        lockPosition: 'left',
                    })),
                });
                this.gridApi.moveColumns(leading, 0);
            },
            getHiddenColIds() {
                return this.columnDefs.filter(a => a.colId && a.hide).map(a => a.colId);
            },
            applyColumnOrderToGrid() {
                if (!this.gridApi || !this.column_state?.columnOrder?.length)
                    return;

                const colState = this.gridApi.getColumnState();
                const fullOrder = this.column_state.columnOrder;
                const leading = this.leadingPinnedColIds
                    .map(id => colState.find(c => c.colId === id))
                    .filter(Boolean)
                    .map(c => ({ ...c, pinned: 'left', lockPosition: 'left' }));
                const leadingIds = new Set(leading.map(c => c.colId));
                const finalState = fullOrder.map(id => colState.find(c => c.colId === id)).filter(Boolean);
                for (const c of colState) {
                    if (!fullOrder.includes(c.colId) && !leadingIds.has(c.colId))
                        finalState.push(c);
                }
                this.gridApi.applyColumnState({
                    state: [...leading, ...finalState.filter(c => !leadingIds.has(c.colId))],
                    applyOrder: true,
                });
            },
            buildGridColumnState(hiddenColIds) {
                const columnOrder = this.column_state?.columnOrder || [];
                const hiddenColIdsResolved = hiddenColIds || this.getHiddenColIds();
                const orderedColIds = [...columnOrder];
                for (const id of hiddenColIdsResolved) {
                    if (!orderedColIds.includes(id)) {
                        orderedColIds.push(id);
                    }
                }
                return {
                    columnOrder: { orderedColIds },
                    columnVisibility: { hiddenColIds: hiddenColIdsResolved }
                };
            },
            mergeColumnOrder(visibleOrder) {
                const priorOrder = this.column_state?.columnOrder || [];
                if (!priorOrder.length)
                    return [...visibleOrder];

                const visibleSet = new Set(visibleOrder);
                const result = [];
                let visibleIdx = 0;

                for (const id of priorOrder) {
                    const cdef = this.columnDefs.find(c => c.colId === id);
                    if (cdef?.hide || !visibleSet.has(id)) {
                        if (!result.includes(id))
                            result.push(id);
                    } else if (visibleIdx < visibleOrder.length) {
                        result.push(visibleOrder[visibleIdx++]);
                    }
                }

                while (visibleIdx < visibleOrder.length) {
                    const id = visibleOrder[visibleIdx++];
                    if (!result.includes(id))
                        result.push(id);
                }

                return result;
            },
            onColumnMoved(e) {
                // finished is true when user releases column drag. 
                // source !== 'api' prevents trigger loop loops.
                if (e.finished && e.source !== 'api' && !this.isPillReordering) {
                    const visiblecols = this.columnDefs.filter(a => this.isReportDataColumn(a) && !a.hide).map(a => a.colId);
                    const colState = this.gridApi.getColumnState();
                    
                    // Sync our internal serialized component structure with grid ordering sequence
                    const newVisibleOrder = colState.map(c => c.colId).filter(a => visiblecols.includes(a));
                    this.column_state.columnOrder = this.mergeColumnOrder(newVisibleOrder);
                }
            },
            columnsordered(newCols) {
                setTimeout(() => {
                    this.isPillReordering = true;
                    if (this.gridApi) {
                        const newOrderedColIds = newCols.map(c => c.colId);

                        this.column_state.columnOrder = this.mergeColumnOrder(newOrderedColIds);

                        this.applyColumnOrderToGrid();
                    }
                    this.modelChanged();
                    
                    setTimeout(() => { this.isPillReordering = false; }, 250);
                }, 0);
            },
            stateUpdated(e) {
                if (!e.sources.some(a => ['columnOrder', 'columnPinning', '_columnSizing', 'sort'].some(b => a == b)))
                    return;

                const visiblecols = this.columnDefs.filter(a => this.isReportDataColumn(a) && !a.hide).map(a => a.colId);

                const state = this.column_state || {};
                if (e.sources.includes('columnSizing'))
                    state.columnSizing = e.state.columnSizing.columnSizingModel.filter(a => visiblecols.includes(a.colId) && a.width != 150);
                
                if (e.sources.includes('columnPinning')) {
                    state.leftPinned = e.state.columnPinning.leftColIds.filter(a => visiblecols.includes(a));
                    state.rightPinned = e.state.columnPinning.rightColIds.filter(a => visiblecols.includes(a));
                }
                this.column_state = state;

                this.modelChanged();
            },
            onFirstDataRendered() {
                this.ensureLeadingPinnedColumns();
                this.gridApi.autoSizeAllColumns();
                this.gridApi.refreshToolPanel();
            },

            dateFormatter(value) {
                return this.f_date(value, 'medium');
            },
            durationFormatter(value) {
                if (typeof value === 'number')
                    return this.f_number(value);
                else
                    value;
            },
            phonenumFormatter(value) {
                return this.f_phoneNumber(value);
            },
            booleanFormatter(params) {
                const val = String(params.value).toLowerCase() === 'true';
                let display = val ? this.Translate('True') : this.Translate('False');

                if (params.colDef && params.colDef.colId && common.column_lookup) {
                    const c = common.column_lookup[params.colDef.colId];
                    if (c && c.lookups) {
                        const key = String(val);
                        if (c.lookups[key] !== undefined)
                            display = c.lookups[key];
                    }
                }
                return display;
            },
            applyDateRangeHandler(range) {

                if (new Date(range.endDate) > new Date(range.startDate)) {
                    this.settings.startDate = range.startDate;
                    this.settings.endDate = range.endDate;
                }
                else {
                    this.settings.startDate = range.endDate;
                    this.settings.endDate = range.startDate;
                }

                utils.log(`Saving localStorage(${this.storageKey}-daterange, ${this.settings.startDate} - ${this.settings.endDate})`);
                this.System.LocalStorage(this.storageKey + '-daterange', `${this.settings.startDate} - ${this.settings.endDate}`);

                this.useStartEnd = true;
                this.Refresh();
            },
            clearDateRangeHandler() {
            },
            clearSearch(){
                this.search = '';
                this.Refresh();
            },

            clearAllFilters() {
                this.EventBus.$emit('ClearAllReportFilters');
            },

            getRowClass(params) {
                if (this.treeData && params.data && params.data.child_count < 0)
                    return 'child-row';
            },
            isServerSideGroup(dataItem) {
                return dataItem.child_count > 0;
            },
            getServerSideGroupKey(dataItem){
                return dataItem.masterid;
            },

            autoSizeAllColumns() {
                this.gridApi.autoSizeAllColumns();
            },
            sizeColumnsToFit() {
                this.gridApi.sizeColumnsToFit();
            },
            setColumnsVisible(cols, value) {
                for (let i = 0; i < this.columnDefs.length; i++) {
                    const c = this.columnDefs[i];
                    if (cols.includes(c.colId))
                    {

                        c.hide = !value;
                    }                                                                
                }
                this.visibleColumnIds = this.columnDefs.filter(a => this.isReportDataColumn(a) && !a.hide).map(a => a.colId);

                console.log(`setColumnsVisible(cols:${JSON.stringify(cols)}, value:${value})`);
                this.gridApi.setColumnsVisible(cols, value);
                this.applyColumnOrderToGrid();

                this.modelChanged();

                if (this.ReadOnly) {
                    // In view mode, save the column visibility to local storage
                    const colstate = this.viewonly_settings.column_picker_list
                        .map(a => this.columnDefs.find(b => b.colId == a.ColId))
                        .map(c => ({ colId: c.colId, visible: !c.hide }));

                    this.System.LocalStorage(this.storageKey + '-columnsvisible', JSON.stringify(colstate));
                }
                // In edit mode we may need more data when displaying a new column
                else if (value)
                    this.Refresh();
            },
            setColumnsDefaultHidden(colid, hidden) {
                if (hidden)
                    this.column_default_visibility[colid] = 1;
                else
                    delete this.column_default_visibility[colid];

                this.modelChanged();
            },

            reportNameChanged(newvalue) {
                //this.report_name = newvalue;
                if (this.setReportNameFunction)
                    this.setReportNameFunction(newvalue, this, helps, Vue);

                this.modelChanged();
            },
            reportDescChanged(newvalue) {
                //this.report_description = newvalue;
                if (this.setReportDescriptionFunction)
                    this.setReportDescriptionFunction(newvalue, this, helps, Vue);

                this.modelChanged();
            },
            reportCategoryChanged(newvalue) {
                this.report_category = newvalue;
                if (this.setReportCategoryFunction)
                    this.setReportCategoryFunction(newvalue, this, helps, Vue);

                this.modelChanged();
            },

            showColumnSelector() {
                this.panelactive = 0;
                this.tool_visible = 0;
            },
            showDataFilters() {
                this.tool_visible = 1;
            },
            toggleDataFilters() {
                if (this.tool_visible == 1)
                    this.tool_visible = undefined;
                else
                    this.tool_visible = 1;
            },
            ClearSelectedRows() {
                if (this.selectedRows)
                    this.selectedRows = [];
                
                this.gridApi?.deselectAll();
            },
            cycleDurationFormats() {
                this.durationFormatIdx = (this.durationFormatIdx + 1) % 3;
                this.gridApi.refreshCells();
            },
        },
        watch: {
            treeData: function (newvalue) {
                this.gridApi.setColumnsVisible(['starttime'], !newvalue);
                this.autoGroupColumnDef = newvalue ?
                    this.groupColDef_byMasterID :
                    this.groupColDef_Normal;
            },
            //source_selected: function (newv) {
            //    this.loadColumns(newv, true);
            //},
            search: function (newvalue) {
                const self = this;
                _.debounce(function () { self.Refresh(); }, 250)();

            },
            TimeZone: function (newvalue) {
                if (newvalue)
                    this.Refresh();
            },
        }
    };
</script>

<style>
    .left-pad-4 {
        padding-left: 4px;
    }
    .c-AdvancedHistory.selection-locked .ag-selection-checkbox {
        pointer-events: none;
        opacity: 0.5;
    }
    /* Selected rows held by an open Ask AI take a muted shade */
    .c-AdvancedHistory.selection-locked .ag-row {
        --ag-selected-row-background-color: #E7EAF2;
    }
    .child-row {
        background-color: cornsilk !important;
    }
    .ag-header-cell-label {
        justify-content: flex-start !important;
    }
    .right-aligned-cell {
        text-align: right;
        flex-direction: column-reverse;
    }
    .icon-spinner {
        animation: spin-animation 1.5s infinite;
        display: inline-block;
    }
    .standard-input-field {
        background-color: white;
    }

    @keyframes spin-animation {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(359deg);
        }
    }
    
</style>

<style scoped>
    .expandIconSpoof {
        transition: transform .3s ease-in-out !important; /* spoofing expansion pannel action icon. Needed to allow positioning of "Clear All" button */
    }
    .expandIconSpoof.rotate {
        transform: rotate(-180deg); /* spoofing expansion pannel action icon. Needed to allow positioning of "Clear All" button */
    }
    .consistentText {
        color: var(--v-black-base) !important; 
        opacity: 0.8 !important; 
        font-size: 14px !important; 
        text-rendering: optimizelegibility !important; 
        letter-spacing:0.1 !important;
    }
</style>
